/**
 * OVERVOLT board renderer — "synthwave meets system diagnostics".
 *
 * Draws the playfield below the HUD strip: an etched circuit-board backdrop,
 * the glowing data path, towers, Glitches, beam effects and particles.
 *
 * Performance notes:
 *  - The *static* board (gradient, circuit etching, grid, path fills, vignette)
 *    is baked once into an offscreen canvas and blitted each frame.
 *  - Neon bloom uses cached radial "glow sprites" composited with `lighter`
 *    rather than per-shape `shadowBlur`, which keeps a busy combat frame at
 *    60fps (canvas `shadowBlur` is far too slow to use by the hundred).
 *
 * This layer reads game state and never mutates it; animation timing uses the
 * wall clock, which is fine because rendering is outside the deterministic
 * simulation contract.
 */
import { CONFIG } from '../config';
import type { AbilityId, TowerType } from '../config';
import type { Audio } from '../engine/audio';
import { clamp, mulberry32, roundRect, withAlpha } from '../engine/util';
import { t } from '../i18n';
import { enemyRadius, type Enemy, type ProjectileKind, type Tower } from './entities';
import { drawJuiceWorld, juiceShakeOffset, trackJuice } from './juice';
import {
  CORE_CELL,
  PATH,
  SPAWN_CELL,
  activeMap,
  activeMapId,
  cellCenter,
  inBounds,
  isBuildable,
  isEndpoint,
  pointAtPath,
  pointToCell,
  type Cell,
} from './map';
import { findPath, tileIndex } from './pathfinding';
import type { GameState } from './state';
import { arcDamageMult, placementReject } from './systems';

/** Playfield pixel origin (top-left), below the HUD strip. */
const OY = CONFIG.hudHeight;
/** Tile size, playfield width / height in pixels. */
const TS = CONFIG.grid.tileSize;
const PW = CONFIG.grid.cols * TS;
const PH = CONFIG.grid.rows * TS;

/** Extended neon palette — base hues come from config; these are the shades. */
const C = CONFIG.colors;
const INK = {
  bgTop: '#070a14',
  bgMid: '#0a0f1d',
  bgBot: '#0d1426',
  trace: '#16243f',
  traceLit: '#1d3b59',
  pad: '#28406a',
  via: '#3a5d8c',
  panel: '#0e1730',
};

/** Display + monospace neon UI fonts (mirrors the HUD layer). */
const FONT_DISPLAY = '"Chakra Petch", "Rajdhani", system-ui, sans-serif';
const FONT_MONO = '"Share Tech Mono", ui-monospace, "DejaVu Sans Mono", monospace';

/** Per-ability signature accent used by the ability VFX. */
const ABILITY_COLOR: Record<AbilityId, string> = {
  overcharge: '#2de2e6',
  airstrike: '#ff8c42',
  empBurst: '#bfe6ff',
};

/** A 2D point. */
interface Pt {
  x: number;
  y: number;
}

// --- per-frame scratch (P4, Sprint 38 perf-wins — docs/PERFORMANCE_AUDIT.md §2.2)
//
// Two render-frame allocations were the largest GC offenders in the §2.2
// audit:
//   1. `new Map<number, Pt>()` at the top of `renderGame` — one Map per frame
//      (60/sec).
//   2. The per-enemy `{ x: p.x, y: p.y + OY }` literal that filled it — up to
//      ~60 Pt objects per frame, 3 600/sec at heavy load.
//
// Both are reused via module-local scratch:
//   - `_enemyPosScratch` is a single Map cleared at the top of each frame and
//     handed to every downstream helper unchanged.
//   - `_enemyPtPool` grows as needed (one Pt per enemy slot) and is reused
//     across frames; we write into pooled Pts rather than allocating new ones.
//
// Both helpers are READ-ONLY in their consumers (`trackVfx`, `drawTowers`,
// `drawEnemies`, …), so a reused Map / Pt is byte-equivalent to a fresh
// allocation — `pos.x` / `pos.y` carry the same pixel values into every
// draw call.
const _enemyPosScratch = new Map<number, Pt>();
const _enemyPtPool: Pt[] = [];

/** Linear interpolation. */
function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

/** Tiny deterministic hash → [0,1). Used for stable per-effect jitter. */
function hash01(n: number): number {
  return mulberry32((n ^ 0x9e3779b9) >>> 0);
}

/**
 * True when (col, row) is a candidate tower tile for the current mode — the
 * fixed map excludes the path; Maze Mode opens every tile but the endpoints.
 * A tile-legality check only (occupancy / affordability / wall-off are extra).
 */
function tilePlaceable(state: GameState, col: number, row: number): boolean {
  if (state.mode === 'maze') return inBounds(col, row) && !isEndpoint(col, row);
  return isBuildable(col, row);
}

/** Order in which a Cipher sheds its eight armor plates — scattered, not clumped. */
const CIPHER_PLATE_ORDER: readonly number[] = [0, 4, 2, 6, 1, 5, 3, 7];

// --- maze route reshape tracking ---------------------------------------------

/**
 * Maze Mode's live route is recomputed by the simulation on every tower
 * place / sell (docs §16.3). The renderer only reads state, so it diffs the
 * route's content between frames: when it changes, an "energising" surge
 * animation sweeps the new trace — the centrepiece of watching the maze
 * reshape. See {@link trackMazePath} / {@link drawMazeReshapeSurge}.
 */

/** Lifetime of the route-reshape energising animation, seconds. */
const MAZE_RESHAPE_LIFE = 0.62;

/** Serialised content key of the last-seen global route — detects a reshape. */
let mazePathKey = '';
/** Wall-clock birth time of the active route-reshape animation, seconds. */
let mazeReshapeBorn = -10;
/**
 * Tile length of the route a valid maze placement preview would produce this
 * frame, or `-1` when nothing is being previewed. Set by `drawPlacementGhost`,
 * read by `drawMazeRouteBadge` so the badge can show a `current → next` hint.
 */
let mazePreviewLen = -1;

/** Compact content key for a tile polyline. */
function routeKey(path: readonly Cell[]): string {
  let k = '';
  for (const c of path) k += `${c.col},${c.row};`;
  return k;
}

/**
 * Watch the global route for a Maze-mode reshape. When `state.path` changes —
 * a tower placed or sold reshaped the maze — arm the energising animation. The
 * first sighting only records the key, so a run never opens mid-animation.
 */
function trackMazePath(state: GameState, time: number): void {
  if (state.mode !== 'maze') {
    mazePathKey = '';
    return;
  }
  const key = routeKey(state.path);
  if (key === mazePathKey) return;
  if (mazePathKey !== '') mazeReshapeBorn = time;
  mazePathKey = key;
}

/** Reshape animation progress 0→1 (1 = settled). */
function mazeReshapeProgress(time: number): number {
  return clamp((time - mazeReshapeBorn) / MAZE_RESHAPE_LIFE, 0, 1);
}

/** Trace a regular hexagon subpath. */
function hexPath(
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  r: number,
  rot: number,
): void {
  ctx.beginPath();
  for (let i = 0; i < 6; i += 1) {
    const a = rot + (Math.PI / 3) * i;
    const px = cx + Math.cos(a) * r;
    const py = cy + Math.sin(a) * r;
    if (i === 0) ctx.moveTo(px, py);
    else ctx.lineTo(px, py);
  }
  ctx.closePath();
}

// --- cached glow sprites -----------------------------------------------------

const glowCache = new Map<string, HTMLCanvasElement>();

/** A cached 128px radial glow sprite for `color` (transparent at the rim). */
function glowSprite(color: string): HTMLCanvasElement {
  let sprite = glowCache.get(color);
  if (sprite) return sprite;
  sprite = document.createElement('canvas');
  sprite.width = 128;
  sprite.height = 128;
  const g = sprite.getContext('2d');
  if (g) {
    const rg = g.createRadialGradient(64, 64, 0, 64, 64, 64);
    rg.addColorStop(0, withAlpha(color, 0.85));
    rg.addColorStop(0.35, withAlpha(color, 0.32));
    rg.addColorStop(1, withAlpha(color, 0));
    g.fillStyle = rg;
    g.fillRect(0, 0, 128, 128);
  }
  glowCache.set(color, sprite);
  return sprite;
}

/** Additively bloom `color` around (x, y). `radius` is the glow's half-size. */
function glow(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  radius: number,
  color: string,
  alpha: number,
): void {
  ctx.save();
  ctx.globalCompositeOperation = 'lighter';
  ctx.globalAlpha = clamp(alpha, 0, 1);
  ctx.drawImage(glowSprite(color), x - radius, y - radius, radius * 2, radius * 2);
  ctx.restore();
}

/** A crisp dot wrapped in a soft additive halo. */
function glowDot(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  r: number,
  core: string,
  halo: string,
): void {
  glow(ctx, x, y, r * 3.4, halo, 0.85);
  ctx.fillStyle = core;
  ctx.beginPath();
  ctx.arc(x, y, r, 0, Math.PI * 2);
  ctx.fill();
}

// --- baked static backdrop ---------------------------------------------------

/**
 * Cached backdrops, keyed by `<mapId>:<maze>` — the path bed, blocked-tile
 * etching and corner auras all depend on the active map (Sprint 21), so we
 * key the cache on the map id and the maze toggle together. Switching the
 * active map is rare (between runs) and the previous backdrop is GC-eligible.
 */
const backdrops = new Map<string, HTMLCanvasElement>();

/**
 * Sprint 21 — drop every cached backdrop. Called by the orchestrator after
 * `setActiveMap` (the path bed / blocked-tile etching changes) so the next
 * render rebuilds against the new map.
 */
export function invalidateBackdrop(): void {
  backdrops.clear();
}

function backdropKey(maze: boolean): string {
  return `${activeMapId()}:${maze ? 'maze' : 'fixed'}`;
}

/**
 * Build (once per layout) the offscreen circuit-board backdrop. The fixed map
 * bakes its recessed path bed; the Maze field is open — its route is emergent
 * and stroked live each frame, so only the endpoints are excluded.
 */
function getBackdrop(maze: boolean): HTMLCanvasElement {
  const key = backdropKey(maze);
  const cached = backdrops.get(key);
  if (cached) return cached;
  const cv = document.createElement('canvas');
  cv.width = PW;
  cv.height = PH;
  const g = cv.getContext('2d');
  backdrops.set(key, cv);
  if (!g) return cv;

  // Base vertical gradient.
  const grad = g.createLinearGradient(0, 0, 0, PH);
  grad.addColorStop(0, INK.bgTop);
  grad.addColorStop(0.55, INK.bgMid);
  grad.addColorStop(1, INK.bgBot);
  g.fillStyle = grad;
  g.fillRect(0, 0, PW, PH);

  // Soft corner auras: cyan at spawn, magenta at core.
  paintAura(g, cellCenter(SPAWN_CELL.col, SPAWN_CELL.row), C.spawn, 260);
  paintAura(g, cellCenter(CORE_CELL.col, CORE_CELL.row), C.core, 300);

  // Etched circuit traces + solder pads.
  paintCircuitry(g);

  // Reference grid. The Maze field is one open build lattice, so its grid
  // reads a touch brighter — the grid *is* the structure there.
  g.strokeStyle = maze ? 'rgba(45, 226, 230, 0.13)' : 'rgba(40, 64, 110, 0.22)';
  g.lineWidth = 1;
  g.beginPath();
  for (let c = 0; c <= CONFIG.grid.cols; c += 1) {
    g.moveTo(c * TS + 0.5, 0);
    g.lineTo(c * TS + 0.5, PH);
  }
  for (let r = 0; r <= CONFIG.grid.rows; r += 1) {
    g.moveTo(0, r * TS + 0.5);
    g.lineTo(PW, r * TS + 0.5);
  }
  g.stroke();

  if (maze) {
    // Maze field: every tile but the two endpoints is a build socket. A small
    // cross-hair "pad" on each reads the whole field as one open lattice
    // (docs §16.5) — visibly distinct from the fixed map's recessed path.
    paintMazeLattice(g);
  } else {
    // Fixed map: a faint solder dot marks each buildable (off-path) tile.
    g.fillStyle = 'rgba(90, 130, 190, 0.30)';
    for (let row = 0; row < CONFIG.grid.rows; row += 1) {
      for (let col = 0; col < CONFIG.grid.cols; col += 1) {
        if (!isBuildable(col, row)) continue;
        const p = cellCenter(col, row);
        g.fillRect(p.x - 1, p.y - 1, 2, 2);
      }
    }
    // The data path: recessed tiles + a dim base trace.
    paintPathBed(g);
  }
  // Sprint 21 — paint the active map's blocked terrain on top of the lattice /
  // path bed, in both maze and fixed-path mode. Empty on the default map.
  paintBlockedTerrain(g);

  // CRT vignette.
  const vig = g.createRadialGradient(PW / 2, PH / 2, PH * 0.35, PW / 2, PH / 2, PH * 0.95);
  vig.addColorStop(0, 'rgba(0,0,0,0)');
  vig.addColorStop(1, 'rgba(0,0,0,0.55)');
  g.fillStyle = vig;
  g.fillRect(0, 0, PW, PH);

  return cv;
}

/** A soft radial wash of `color` around `at`. */
function paintAura(g: CanvasRenderingContext2D, at: Pt, color: string, radius: number): void {
  const rg = g.createRadialGradient(at.x, at.y, 0, at.x, at.y, radius);
  rg.addColorStop(0, withAlpha(color, 0.16));
  rg.addColorStop(1, withAlpha(color, 0));
  g.fillStyle = rg;
  g.fillRect(0, 0, PW, PH);
}

/** Etch a deterministic web of PCB traces, pads and vias into the backdrop. */
function paintCircuitry(g: CanvasRenderingContext2D): void {
  let seed = 0x07ce27a1 >>> 0;
  const rng = (): number => {
    seed = (seed + 0x6d2b79f5) >>> 0;
    return mulberry32(seed);
  };
  for (let i = 0; i < 30; i += 1) {
    let x = (Math.floor(rng() * CONFIG.grid.cols) + 0.5) * TS;
    let y = (Math.floor(rng() * CONFIG.grid.rows) + 0.5) * TS;
    const pts: Pt[] = [{ x, y }];
    const segs = 2 + Math.floor(rng() * 4);
    for (let s = 0; s < segs; s += 1) {
      const len = (1 + Math.floor(rng() * 4)) * TS;
      if (s % 2 === 0) x += rng() < 0.5 ? len : -len;
      else y += rng() < 0.5 ? len : -len;
      x = clamp(x, TS * 0.5, PW - TS * 0.5);
      y = clamp(y, TS * 0.5, PH - TS * 0.5);
      pts.push({ x, y });
    }
    const lit = rng() < 0.28;
    g.strokeStyle = lit ? INK.traceLit : INK.trace;
    g.lineWidth = lit ? 2 : 1.4;
    g.lineJoin = 'round';
    g.beginPath();
    pts.forEach((p, idx) => (idx === 0 ? g.moveTo(p.x, p.y) : g.lineTo(p.x, p.y)));
    g.stroke();
    // Solder pads at the bends.
    g.fillStyle = INK.pad;
    for (const p of pts) g.fillRect(p.x - 2, p.y - 2, 4, 4);
    // A via ring at the trace head.
    g.strokeStyle = INK.via;
    g.lineWidth = 1.4;
    g.beginPath();
    g.arc(pts[0]!.x, pts[0]!.y, 3.4, 0, Math.PI * 2);
    g.stroke();
  }
}

/**
 * Sprint 21 — paint the active map's blocked terrain as solid slate-grey
 * tiles with a hatched "no build" cross overlay. Drawn into the baked
 * backdrop so blocked tiles read as permanent terrain (vs. towers, which
 * draw live on top). Empty on the default map's empty `blocked` set.
 */
function paintBlockedTerrain(g: CanvasRenderingContext2D): void {
  const map = activeMap();
  if (map.blocked.length === 0) return;
  for (const cell of map.blocked) {
    const x = cell.col * TS;
    const y = cell.row * TS;
    // Tile body — dark slate, slightly inset.
    const tg = g.createLinearGradient(0, y, 0, y + TS);
    tg.addColorStop(0, '#1a2238');
    tg.addColorStop(1, '#0e1322');
    g.fillStyle = tg;
    roundRect(g, x + 1.5, y + 1.5, TS - 3, TS - 3, 4);
    g.fill();
    // Stipple border for the "industrial" terrain read.
    g.strokeStyle = 'rgba(120, 140, 180, 0.45)';
    g.lineWidth = 1;
    g.stroke();
    // Subtle diagonal hatching — keeps the tile visually distinct from a
    // recessed path bed and a placed tower. Clipped to the tile so strokes
    // never spill into the neighbour.
    g.save();
    g.beginPath();
    g.rect(x + 2, y + 2, TS - 4, TS - 4);
    g.clip();
    g.strokeStyle = 'rgba(150, 170, 210, 0.18)';
    g.lineWidth = 1;
    g.beginPath();
    for (let off = -TS; off < TS * 2; off += 6) {
      g.moveTo(x + off, y);
      g.lineTo(x + off + TS, y + TS);
    }
    g.stroke();
    g.restore();
  }
}

/** Recess the path tiles and lay a dim base trace down their centre. */
function paintPathBed(g: CanvasRenderingContext2D): void {
  for (const cell of PATH) {
    const x = cell.col * TS;
    const y = cell.row * TS;
    const tg = g.createLinearGradient(0, y, 0, y + TS);
    tg.addColorStop(0, '#0e2a44');
    tg.addColorStop(1, '#0a1c30');
    g.fillStyle = tg;
    roundRect(g, x + 1.5, y + 1.5, TS - 3, TS - 3, 7);
    g.fill();
    g.strokeStyle = withAlpha(C.pathGlow, 0.16);
    g.lineWidth = 1;
    g.stroke();
  }
  // Dim base centreline (the bright animated layer is drawn live).
  g.lineJoin = 'round';
  g.lineCap = 'round';
  g.strokeStyle = withAlpha(C.pathGlow, 0.13);
  g.lineWidth = 9;
  strokePath(g);
  g.strokeStyle = withAlpha(C.pathGlow, 0.22);
  g.lineWidth = 3;
  strokePath(g);
}

/**
 * Bake the Maze field's build lattice: a small cross-hair "pad" on every
 * non-endpoint tile, so the whole open field reads as one buildable grid
 * (docs §16.5), plus a hex socket framing each fixed endpoint.
 */
function paintMazeLattice(g: CanvasRenderingContext2D): void {
  // Cross-hair build pads — one batched stroke for every socket.
  g.strokeStyle = 'rgba(86, 140, 200, 0.30)';
  g.lineWidth = 1;
  g.beginPath();
  for (let row = 0; row < CONFIG.grid.rows; row += 1) {
    for (let col = 0; col < CONFIG.grid.cols; col += 1) {
      if (isEndpoint(col, row)) continue;
      const p = cellCenter(col, row);
      g.moveTo(p.x - 3.5, p.y + 0.5);
      g.lineTo(p.x + 3.5, p.y + 0.5);
      g.moveTo(p.x + 0.5, p.y - 3.5);
      g.lineTo(p.x + 0.5, p.y + 3.5);
    }
  }
  g.stroke();
  // A solder bead at each pad centre.
  g.fillStyle = 'rgba(120, 160, 220, 0.42)';
  for (let row = 0; row < CONFIG.grid.rows; row += 1) {
    for (let col = 0; col < CONFIG.grid.cols; col += 1) {
      if (isEndpoint(col, row)) continue;
      const p = cellCenter(col, row);
      g.fillRect(p.x - 1, p.y - 1, 2, 2);
    }
  }
  // Endpoint sockets — hex frames baked under the live spawn portal / Core.
  for (const cell of [SPAWN_CELL, CORE_CELL]) {
    const p = cellCenter(cell.col, cell.row);
    const color = cell === SPAWN_CELL ? C.spawn : C.core;
    g.strokeStyle = withAlpha(color, 0.34);
    g.lineWidth = 1.5;
    hexPath(g, p.x, p.y, TS * 0.4, -Math.PI / 2);
    g.stroke();
  }
}

/** Stroke an arbitrary tile polyline (caller sets style); coordinates are local. */
function strokePolyline(g: CanvasRenderingContext2D, path: readonly Cell[]): void {
  g.beginPath();
  path.forEach((cell, i) => {
    const p = cellCenter(cell.col, cell.row);
    if (i === 0) g.moveTo(p.x, p.y);
    else g.lineTo(p.x, p.y);
  });
  g.stroke();
}

/** Stroke the fixed S-path polyline (caller sets style); coordinates are local. */
function strokePath(g: CanvasRenderingContext2D): void {
  strokePolyline(g, PATH);
}

/**
 * Stroke a sub-span of a tile polyline between two fractional distances
 * `[d0, d1]` (tiles). Used by the maze-reshape surge to brighten just the
 * stretch of trace behind the travelling energy head.
 */
function strokeRouteSegment(
  g: CanvasRenderingContext2D,
  path: readonly Cell[],
  d0: number,
  d1: number,
): void {
  const maxD = path.length - 1;
  const lo = clamp(Math.min(d0, d1), 0, maxD);
  const hi = clamp(Math.max(d0, d1), 0, maxD);
  if (hi - lo < 1e-3) return;
  g.beginPath();
  const a = pointAtPath(path, lo);
  g.moveTo(a.x, a.y);
  for (let i = Math.ceil(lo); i <= Math.floor(hi); i += 1) {
    const c = path[i]!;
    const p = cellCenter(c.col, c.row);
    g.lineTo(p.x, p.y);
  }
  const b = pointAtPath(path, hi);
  g.lineTo(b.x, b.y);
  g.stroke();
}

// --- public entry ------------------------------------------------------------

/**
 * Render the whole board.
 * @param tickProgress 0..1 progress between logical ticks (smooths motion).
 * @param audio        audio sink for the §18 juice-layer boss SFX.
 */
export function renderGame(
  ctx: CanvasRenderingContext2D,
  state: GameState,
  pointer: { x: number; y: number },
  tickProgress: number,
  audio: Audio,
): void {
  const time = performance.now() / 1000;

  // Live screen position of every enemy (shared by towers + enemy draw).
  // P4 (Sprint 38 perf-wins, docs/PERFORMANCE_AUDIT.md §2.2): the Map *and*
  // every per-enemy Pt are reused from module-local scratch — see
  // `_enemyPosScratch` / `_enemyPtPool` above. Byte-equivalent to the
  // pre-pool implementation (consumers only read `pos.x` / `pos.y`).
  const enemyPos = _enemyPosScratch;
  enemyPos.clear();
  let poolIdx = 0;
  for (const e of state.enemies) {
    if (!e.alive) continue;
    const d = lerp(e.prevPathDist, e.pathDist, tickProgress);
    // Each enemy interpolates along its own route — the fixed S-path, or its
    // personal Maze A* route (docs §16.6).
    const p = pointAtPath(e.path, d);
    let pt = _enemyPtPool[poolIdx];
    if (pt === undefined) {
      pt = { x: 0, y: 0 };
      _enemyPtPool[poolIdx] = pt;
    }
    pt.x = p.x;
    pt.y = p.y + OY;
    enemyPos.set(e.id, pt);
    poolIdx += 1;
  }

  // Detect tick-driven events (Cryo pulses, Fork splits) for transient VFX.
  trackVfx(state, enemyPos, time);
  // Detect Patch heal pulses for the repair-aura + green heal flashes.
  trackHeal(state, time);
  // Detect a Maze-mode route reshape to arm the energising surge animation.
  trackMazePath(state, time);
  // Detect a fresh Kernel to arm the "⚠ KERNEL INCOMING" banner.
  trackBoss(state, time);
  // Cleared each frame; `drawPlacementGhost` sets it when a preview is live.
  mazePreviewLen = -1;

  // §18 juice layer — render-/audio-only, gated by `juiceEnabled`. It diffs the
  // run for damage numbers / shake / hit-stop / boss SFX, all on a dedicated
  // render-only RNG, so it never perturbs the sim or a QA golden (docs §18).
  const juice = CONFIG.juiceEnabled;
  if (juice) trackJuice(state, enemyPos, time, audio);
  const shake = juice ? juiceShakeOffset(time) : { x: 0, y: 0 };

  ctx.save();
  // Screen shake offsets the *playfield* render transform; the HUD strip and
  // the boss HP bar are drawn outside it, so they never shake (readability).
  ctx.translate(shake.x, shake.y);
  ctx.drawImage(getBackdrop(state.mode === 'maze'), 0, OY);
  drawPathEnergy(ctx, state, time);
  drawNodes(ctx, state, time);
  drawRangeRings(ctx, state, pointer, time);
  drawTowers(ctx, state, time, enemyPos);
  // Sprint 20 (abilities-s20b): Airstrike scorch zones — the lingering kill-
  // zone half of the proactive re-role (docs/GAME_DESIGN.md §15). Drawn UNDER
  // the enemies so the burning ground reads as terrain the enemies walk over.
  drawScorchZones(ctx, state, time);
  drawVfx(ctx, time);
  drawEnemies(ctx, state, enemyPos, time);
  // The Kernel's Power-Drain telegraph + active-drain siphon (docs §17.3).
  drawBossDrainFx(ctx, state, enemyPos, time);
  drawProjectiles(ctx, state);
  drawParticles(ctx, state);
  // §18 world-space juice — sparks, kill flashes, the Kernel death explosion,
  // phase-transition flashes and floating damage numbers — all ride the shake.
  if (juice) drawJuiceWorld(ctx, time);
  drawPlacementGhost(ctx, state, pointer, time);
  // The Overload dial and the ability slots moved into the HUD's ABILITIES
  // zone (Sprint 10) — they no longer occlude the playfield.
  if (state.phase === 'build') drawBuildHint(ctx, state, time);
  // Maze Mode: a live route-length readout, top-right of the playfield.
  if (state.mode === 'maze') drawMazeRouteBadge(ctx, state, time);
  if (state.overchargeTicksLeft > 0) drawOvercharge(ctx, state, time);
  // Sprint 20 (abilities-s20b): the Overcharge aftershock brownout — the
  // proactive *cost* of the burst (docs/GAME_DESIGN.md §15). Drawn as a brief
  // dim purple wash so the player *sees* the debt they took on, distinct from
  // the §6 power-grid brownout overlay below.
  if (state.aftershockTicksLeft > 0) drawAftershock(ctx, state, time);
  drawBrownout(ctx, state, time);
  drawScanlines(ctx, time);
  ctx.restore();

  // Boss-fight UI — the dedicated HP bar + the pre-spawn banner. Drawn after
  // the shake transform restores, so this UI layer stays rock-steady (§18.1.6).
  drawBossUi(ctx, state, time);
}

// --- path --------------------------------------------------------------------

/**
 * The animated layer over the route: flowing dashes + data packets. Draws
 * `state.path` — the baked fixed S-path, or the live Maze A* route (§16.3),
 * which has no baked bed so a dim base trace is laid down first.
 */
function drawPathEnergy(ctx: CanvasRenderingContext2D, state: GameState, time: number): void {
  const path = state.path;
  const pathLen = Math.max(1, state.mazePathLength);
  const maze = state.mode === 'maze';
  // Maze route-reshape energising surge: `surge` runs ~0.6 → 0 after a place
  // or sell reshapes the maze (docs §16.3); 0 on a settled / fixed route.
  const reshape = maze ? mazeReshapeProgress(time) : 1;
  const surge = maze ? 1 - reshape : 0;
  ctx.save();
  ctx.translate(0, OY);
  ctx.lineJoin = 'round';
  ctx.lineCap = 'round';

  // Maze Mode has no baked path bed — lay a dim base trace so the emergent
  // route reads clearly against the open field. It flares while reshaping.
  if (maze) {
    ctx.strokeStyle = withAlpha(C.pathGlow, 0.13 + 0.3 * surge);
    ctx.lineWidth = 9 + 5 * surge;
    strokePolyline(ctx, path);
    ctx.strokeStyle = withAlpha(C.pathGlow, 0.22 + 0.35 * surge);
    ctx.lineWidth = 3;
    strokePolyline(ctx, path);
  }

  // Soft pulsing glow underlay (additive translucent stroke).
  ctx.globalCompositeOperation = 'lighter';
  ctx.strokeStyle = withAlpha(C.pathGlow, 0.16 + 0.05 * Math.sin(time * 2) + 0.4 * surge);
  ctx.lineWidth = 7 + 5 * surge;
  strokePolyline(ctx, path);

  // Flowing energy dashes, marching spawn → core — they race a little faster
  // while a freshly-reshaped maze re-energises.
  ctx.strokeStyle = withAlpha(C.pathGlow, 0.9);
  ctx.lineWidth = 2.4;
  ctx.setLineDash([9, 21]);
  ctx.lineDashOffset = -((time * (46 + 90 * surge)) % 30);
  strokePolyline(ctx, path);
  ctx.setLineDash([]);

  // The reshape surge — a bright energy head sweeping the new route.
  if (surge > 0) drawMazeReshapeSurge(ctx, path, pathLen, reshape);

  ctx.globalCompositeOperation = 'source-over';

  // Discrete data packets gliding toward the Core.
  const count = 5;
  for (let i = 0; i < count; i += 1) {
    const d = (time * 5.5 + (i * pathLen) / count) % pathLen;
    const p = pointAtPath(path, d);
    glowDot(ctx, p.x, p.y, 2.4, '#dffcff', C.pathGlow);
  }
  ctx.restore();
}

/**
 * The Maze route-reshape animation: a bright energy head races spawn → Core
 * along the freshly-computed route, brightening the trace behind it — the
 * maze visibly "powering up" its new path after a place / sell (docs §16).
 * Called inside `drawPathEnergy` under an additive (`lighter`) composite.
 */
function drawMazeReshapeSurge(
  ctx: CanvasRenderingContext2D,
  path: readonly Cell[],
  pathLen: number,
  reshape: number,
): void {
  // Ease the head out so it decelerates into the Core.
  const eased = 1 - (1 - reshape) * (1 - reshape);
  const head = eased * pathLen;
  const trail = Math.min(head, pathLen * 0.34 + 1.5);
  const fade = 1 - reshape;
  // The bright stretch of trace just behind the energy head.
  ctx.strokeStyle = withAlpha(C.pathGlow, 0.55 * fade);
  ctx.lineWidth = 10;
  strokeRouteSegment(ctx, path, head - trail, head);
  ctx.strokeStyle = withAlpha('#dffcff', 0.95 * fade);
  ctx.lineWidth = 3;
  strokeRouteSegment(ctx, path, head - trail, head);
  // The energy head itself — a hot white core in a cyan bloom.
  const hp = pointAtPath(path, clamp(head, 0, pathLen));
  glow(ctx, hp.x, hp.y, 22, C.pathGlow, 0.8 * fade);
  ctx.fillStyle = withAlpha('#ffffff', 0.95);
  ctx.beginPath();
  ctx.arc(hp.x, hp.y, 3.4, 0, Math.PI * 2);
  ctx.fill();
}

// --- spawn portal + core reactor --------------------------------------------

/** Draw the spawn portal and the Core reactor. */
function drawNodes(ctx: CanvasRenderingContext2D, state: GameState, time: number): void {
  const sp = cellCenter(SPAWN_CELL.col, SPAWN_CELL.row);
  drawSpawnPortal(ctx, sp.x, sp.y + OY, time);
  const co = cellCenter(CORE_CELL.col, CORE_CELL.row);
  drawCore(ctx, co.x, co.y + OY, state, time);
}

/** Pulsing intake portal where Glitches appear. */
function drawSpawnPortal(ctx: CanvasRenderingContext2D, x: number, y: number, time: number): void {
  glow(ctx, x, y, 34, C.spawn, 0.5);
  ctx.save();
  ctx.translate(x, y);
  // Imploding rings (energy drawn inward).
  ctx.globalCompositeOperation = 'lighter';
  for (let i = 0; i < 3; i += 1) {
    const t = (time * 0.8 + i / 3) % 1;
    ctx.strokeStyle = withAlpha(C.spawn, 0.4 * t);
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.arc(0, 0, lerp(20, 5, t), 0, Math.PI * 2);
    ctx.stroke();
  }
  ctx.globalCompositeOperation = 'source-over';
  // Hex housing.
  ctx.strokeStyle = C.spawn;
  ctx.lineWidth = 2;
  hexPath(ctx, 0, 0, 13, time * 0.5);
  ctx.stroke();
  ctx.restore();
  // Hot core.
  glowDot(ctx, x, y, 5, '#eafdff', C.spawn);
}

/** The Core reactor — the player's last line; flickers red as lives drop. */
function drawCore(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  state: GameState,
  time: number,
): void {
  const integrity = clamp(state.lives / state.startLives, 0, 1);
  const danger = 1 - integrity;
  const flick = danger > 0.55 ? 0.5 + 0.5 * Math.sin(time * 22) : 1;
  const hue = danger > 0.7 ? C.bad : C.core;

  glow(ctx, x, y, 40, hue, 0.45 + 0.3 * flick);
  ctx.save();
  ctx.translate(x, y);
  // Halo rings expanding outward.
  ctx.globalCompositeOperation = 'lighter';
  for (let i = 0; i < 3; i += 1) {
    const t = (time * 0.6 + i / 3) % 1;
    ctx.strokeStyle = withAlpha(hue, 0.32 * (1 - t) * flick);
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.arc(0, 0, lerp(15, 34, t), 0, Math.PI * 2);
    ctx.stroke();
  }
  ctx.globalCompositeOperation = 'source-over';
  // Outer hex shell.
  const shell = ctx.createRadialGradient(0, 0, 2, 0, 0, 16);
  shell.addColorStop(0, withAlpha(hue, 0.95));
  shell.addColorStop(1, withAlpha(hue, 0.18));
  ctx.fillStyle = shell;
  hexPath(ctx, 0, 0, 16, -Math.PI / 2);
  ctx.fill();
  ctx.strokeStyle = hue;
  ctx.lineWidth = 2;
  ctx.stroke();
  // Counter-rotating inner triangle.
  ctx.strokeStyle = '#fff';
  ctx.lineWidth = 1.6;
  ctx.beginPath();
  for (let i = 0; i < 3; i += 1) {
    const a = -time * 1.3 + (Math.PI * 2 * i) / 3;
    const px = Math.cos(a) * 7;
    const py = Math.sin(a) * 7;
    if (i === 0) ctx.moveTo(px, py);
    else ctx.lineTo(px, py);
  }
  ctx.closePath();
  ctx.stroke();
  ctx.restore();
  glowDot(ctx, x, y, 3, '#fff', hue);
}

// --- range rings -------------------------------------------------------------

/** Range rings for the selected tower and the live placement preview. */
function drawRangeRings(
  ctx: CanvasRenderingContext2D,
  state: GameState,
  pointer: { x: number; y: number },
  time: number,
): void {
  const sel = state.towers.find((tw) => tw.id === state.selectedTowerId);
  if (sel && CONFIG.towers[sel.type].weapon) {
    const c = cellCenter(sel.col, sel.row);
    ring(ctx, c.x, c.y + OY, sel.range * TS, CONFIG.towers[sel.type].color, time);
    // The Splash Mortar's inner dead zone — the radius it cannot fire into.
    if (sel.minRange > 0) deadZoneRing(ctx, c.x, c.y + OY, sel.minRange * TS, time);
  }
  // Sprint 39 (REC 3.b — docs/REC3_DESIGN.md §2.B): a selected Pylon shows
  // a soft halo at its aura radius so the player can read "which weapon
  // towers does this Pylon buff?" before committing a sell or an upgrade.
  // Same disc / dashed-boundary class as the Mortar dead-zone ring so the
  // visual vocabulary is consistent.
  if (sel && sel.type === 'pylon') {
    const c = cellCenter(sel.col, sel.row);
    auraRing(ctx, c.x, c.y + OY, CONFIG.pylonAuraRadius * TS, time);
  }
  const armed = state.selectedTowerType;
  if (armed && CONFIG.towers[armed].weapon) {
    const cell = pointToCell(pointer.x, pointer.y - OY);
    if (tilePlaceable(state, cell.col, cell.row)) {
      const c = cellCenter(cell.col, cell.row);
      const range = CONFIG.towers[armed].levels[0].range;
      ring(ctx, c.x, c.y + OY, range * TS, CONFIG.towers[armed].color, time);
      // Preview the Mortar's dead zone too, so the standoff a placement needs
      // is visible before the player commits the tile.
      if (armed === 'mortar') {
        deadZoneRing(ctx, c.x, c.y + OY, CONFIG.towers.mortar.levels[0].minRange * TS, time);
      }
    }
  }
  // Sprint 39 (REC 3.b): placement preview for a Pylon — the aura halo
  // shows the radius at the live cursor tile so the player can pick the
  // tile that touches the most weapon towers *before* spending the gold.
  if (armed === 'pylon') {
    const cell = pointToCell(pointer.x, pointer.y - OY);
    if (tilePlaceable(state, cell.col, cell.row)) {
      const c = cellCenter(cell.col, cell.row);
      auraRing(ctx, c.x, c.y + OY, CONFIG.pylonAuraRadius * TS, time);
    }
  }
}

/**
 * Sprint 39 (REC 3.b — docs/REC3_DESIGN.md §2.B + docs/NEXT_BATCH_PLAN.md
 * §2.6) — the **Pylon aura** halo: a soft yellow disc with a dashed boundary
 * at `pylonAuraRadius` tiles, marking the in-radius zone within which weapon
 * towers receive the `pylonAuraRateBuff` fire-rate buff (stack-capped). Drawn
 * for a selected Pylon AND the placement preview so the player can pick the
 * tile that touches the most weapon towers before committing. Visual class
 * matches the Sprint-14 Mortar dead-zone ring style — same gradient wash +
 * dashed circumference, counter-rotating slowly — so the lever reads as one
 * consistent vocabulary with the other in-tower visualisations.
 */
function auraRing(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  r: number,
  time: number,
): void {
  ctx.save();
  ctx.translate(x, y);
  // Pylon's own neon yellow — re-uses the tower's accent so the ring reads
  // as "this Pylon's reach", not as a new colour the player has to learn.
  const hue = CONFIG.towers.pylon.color;
  const fill = ctx.createRadialGradient(0, 0, 0, 0, 0, r);
  fill.addColorStop(0, withAlpha(hue, 0.18));
  fill.addColorStop(1, withAlpha(hue, 0.04));
  ctx.fillStyle = fill;
  ctx.beginPath();
  ctx.arc(0, 0, r, 0, Math.PI * 2);
  ctx.fill();
  // Dashed boundary, counter-rotating the main range ring's rotation so a
  // selected Pulse + co-selected Pylon (during placement preview) don't
  // pulse in lockstep — same idiom the Mortar dead-zone ring uses.
  ctx.rotate(-time * 0.5);
  ctx.strokeStyle = withAlpha(hue, 0.7);
  ctx.lineWidth = 1.4;
  ctx.setLineDash([4, 6]);
  ctx.beginPath();
  ctx.arc(0, 0, r, 0, Math.PI * 2);
  ctx.stroke();
  ctx.restore();
}

/**
 * The Splash Mortar's inner dead-zone ring — a warning-tinted disc marking the
 * radius the Mortar's ballistic arc cannot reach (docs/GAME_DESIGN.md §5).
 * Drawn inside the normal outer range ring, so together they read as the
 * firing annulus: a target is hittable *between* the two rings, never inside
 * this one.
 */
function deadZoneRing(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  r: number,
  time: number,
): void {
  ctx.save();
  ctx.translate(x, y);
  // Faint warning wash — densest at the tower, fading toward the dead-zone rim.
  const fill = ctx.createRadialGradient(0, 0, 0, 0, 0, r);
  fill.addColorStop(0, withAlpha(C.warning, 0.22));
  fill.addColorStop(1, withAlpha(C.warning, 0.05));
  ctx.fillStyle = fill;
  ctx.beginPath();
  ctx.arc(0, 0, r, 0, Math.PI * 2);
  ctx.fill();
  // Dashed boundary, counter-rotating the outer range ring so the annulus reads.
  ctx.rotate(-time * 0.6);
  ctx.strokeStyle = withAlpha(C.warning, 0.8);
  ctx.lineWidth = 1.5;
  ctx.setLineDash([5, 6]);
  ctx.beginPath();
  ctx.arc(0, 0, r, 0, Math.PI * 2);
  ctx.stroke();
  ctx.restore();
}

/** A translucent, slowly rotating dashed range circle. */
function ring(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  r: number,
  color: string,
  time: number,
): void {
  ctx.save();
  ctx.translate(x, y);
  const fill = ctx.createRadialGradient(0, 0, r * 0.4, 0, 0, r);
  fill.addColorStop(0, withAlpha(color, 0));
  fill.addColorStop(1, withAlpha(color, 0.12));
  ctx.fillStyle = fill;
  ctx.beginPath();
  ctx.arc(0, 0, r, 0, Math.PI * 2);
  ctx.fill();
  ctx.rotate(time * 0.6);
  ctx.strokeStyle = withAlpha(color, 0.7);
  ctx.lineWidth = 1.5;
  ctx.setLineDash([6, 8]);
  ctx.beginPath();
  ctx.arc(0, 0, r, 0, Math.PI * 2);
  ctx.stroke();
  ctx.restore();
}

// --- towers ------------------------------------------------------------------

/**
 * P4 (Sprint 38 perf-wins, docs/PERFORMANCE_AUDIT.md §3.5): per-tower
 * recent-fire flash key. Quantises the projectile origin onto a grid bucket
 * the same way the original nested loop did (`|p.from.x − c.x| < 1.5` ∧
 * `|p.from.y − c.y| < 1.5`). Tower-tile centres land on the nominal cell
 * centre (`col * TS + TS/2`), which is an integer, so rounding to the nearest
 * integer maps every projectile that the original `< 1.5` check would have
 * matched into the same bucket as the firing tower — byte-equivalent flash.
 */
function flashBucketKey(x: number, y: number, kind: ProjectileKind): string {
  return `${Math.round(x)}|${Math.round(y)}|${kind}`;
}

const _flashByBucket = new Map<string, number>();

/** Draw every placed tower with a type-specific neon silhouette. */
function drawTowers(
  ctx: CanvasRenderingContext2D,
  state: GameState,
  time: number,
  enemyPos: Map<number, Pt>,
): void {
  // Overcharge floods every tower with a cyan power surge (docs §11 / §15).
  const oc =
    state.overchargeTicksLeft > 0 ? state.overchargeTicksLeft / overchargeDuration() : 0;
  const ocPulse = 0.6 + 0.4 * Math.sin(time * 9);
  // Arc Coil surplus-coupling visual (docs §5): the live grid surplus charges
  // every Arc the same — +1 at the damage cap, −1 at the brownout floor.
  const arcMult = arcDamageMult(state.power.surplus);
  const arcCharge =
    arcMult >= 1
      ? (arcMult - 1) / CONFIG.arcSurplusDamageCap
      : (arcMult - 1) / (1 - CONFIG.arcBrownoutDamageFloor);
  // P4 §3.5 pre-pass: collapse the O(towers × projectiles) flash lookup into
  // a single O(projectiles) walk that buckets max-flash by (origin, kind).
  // The per-tower lookup below is then an O(1) Map read — byte-equivalent
  // result, one Map allocation reused frame-to-frame.
  _flashByBucket.clear();
  for (const p of state.projectiles) {
    const key = flashBucketKey(p.from.x, p.from.y, p.kind);
    const t = p.ttlTicks / CONFIG.beamTtlTicks;
    const prev = _flashByBucket.get(key);
    if (prev === undefined || t > prev) _flashByBucket.set(key, t);
  }
  for (const tower of state.towers) {
    const c = cellCenter(tower.col, tower.row);
    const cx = c.x;
    const cy = c.y + OY;
    const color = CONFIG.towers[tower.type].color;
    const selected = tower.id === state.selectedTowerId;

    // The Maze-Mode Wall is an inert blocker — it skips the tower mounting
    // pad, the level glow, the Overcharge surge and the level pips (§16.9).
    if (tower.type === 'wall') {
      drawWall(ctx, cx, cy, color, selected);
      continue;
    }

    // Recent-fire flash: a beam originating at this tile means it just shot.
    // O(1) lookup — see the pre-pass + `flashBucketKey` above.
    const flash = _flashByBucket.get(flashBucketKey(c.x, c.y, tower.type)) ?? 0;

    // A tower's glow intensifies with its upgrade level.
    glow(ctx, cx, cy, 20 + tower.level * 3, color, 0.28 + tower.level * 0.07 + flash * 0.4);
    // Sprint 26 (REC 3, docs/REC3_DESIGN.md §4.3): an Overclocked weapon
    // tower wears a pulsing brownout-orange halo — the same `warning` colour
    // the power-bar pulses during a brownout, so the player reads the *cost*
    // (extra draw) in the same colour they already know. Pulse keyed off
    // `state.tick` (deterministic) plus the tower id (so a row of Overclocks
    // doesn't pulse in lockstep). Skipped on non-weapon towers — Pylon /
    // Wall can never set the flag.
    if (tower.overclock && CONFIG.towers[tower.type].weapon) {
      const ocPulseT = 0.6 + 0.4 * Math.sin(state.tick * 0.6 + tower.id);
      glow(ctx, cx, cy, 22, C.warning, 0.42 * ocPulseT);
      ctx.save();
      ctx.globalCompositeOperation = 'lighter';
      ctx.strokeStyle = withAlpha(C.warning, 0.55 + 0.4 * ocPulseT);
      ctx.lineWidth = 1.6;
      ctx.beginPath();
      ctx.arc(cx, cy, 19 + 2.5 * ocPulseT, 0, Math.PI * 2);
      ctx.stroke();
      ctx.restore();
    }
    // Overcharge surge — a cyan halo + crackle ring while the window is live.
    if (oc > 0) {
      glow(ctx, cx, cy, 26, C.accent, 0.34 * oc * ocPulse);
      ctx.save();
      ctx.globalCompositeOperation = 'lighter';
      ctx.strokeStyle = withAlpha(C.accent, (0.4 + 0.4 * ocPulse) * oc);
      ctx.lineWidth = 1.6;
      ctx.beginPath();
      ctx.arc(cx, cy, 17 + 2.5 * ocPulse, 0, Math.PI * 2);
      ctx.stroke();
      ctx.restore();
    }
    drawTowerPad(ctx, cx, cy, color, selected, time);
    if (tower.type === 'pulse') drawPulse(ctx, cx, cy, tower, flash, time, enemyPos);
    else if (tower.type === 'arc') drawArc(ctx, cx, cy, color, flash, time, arcCharge);
    else if (tower.type === 'cryo') drawCryo(ctx, cx, cy, color, flash, time);
    else if (tower.type === 'mortar') drawMortar(ctx, cx, cy, tower, flash, time, enemyPos);
    else if (tower.type === 'sniper') drawSniper(ctx, cx, cy, tower, flash, time, enemyPos);
    else drawPylon(ctx, cx, cy, color, time);
    drawLevelPips(ctx, cx, cy, tower.level, color);
  }
}

/** Three pips beneath a tower marking its upgrade level (1–3). */
function drawLevelPips(
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  level: number,
  color: string,
): void {
  const max = CONFIG.maxTowerLevel;
  const gap = 6.5;
  const y = cy + 19;
  const x0 = cx - (gap * (max - 1)) / 2;
  for (let i = 0; i < max; i += 1) {
    const px = x0 + i * gap;
    const lit = i < level;
    if (lit) glow(ctx, px, y, 4.5, color, 0.7);
    ctx.fillStyle = lit ? '#eef7ff' : 'rgba(120, 150, 200, 0.28)';
    ctx.beginPath();
    ctx.arc(px, y, lit ? 2 : 1.5, 0, Math.PI * 2);
    ctx.fill();
  }
}

/** Cryo Emitter: a slowly rotating frost crystal that radiates cold. */
function drawCryo(
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  color: string,
  flash: number,
  time: number,
): void {
  ctx.save();
  ctx.translate(cx, cy);
  // Cold breathing aura.
  ctx.globalCompositeOperation = 'lighter';
  for (let i = 0; i < 2; i += 1) {
    const t = (time * 0.5 + i / 2) % 1;
    ctx.strokeStyle = withAlpha(color, 0.22 * (1 - t));
    ctx.lineWidth = 1.4;
    ctx.beginPath();
    ctx.arc(0, 0, lerp(7, 18, t), 0, Math.PI * 2);
    ctx.stroke();
  }
  ctx.globalCompositeOperation = 'source-over';
  // Six-armed frost crystal, slowly turning; arms lengthen with the fire flash.
  ctx.rotate(time * 0.45);
  const arm = 11 + flash * 3;
  ctx.strokeStyle = color;
  ctx.lineCap = 'round';
  ctx.lineWidth = 2;
  for (let i = 0; i < 6; i += 1) {
    ctx.save();
    ctx.rotate((Math.PI / 3) * i);
    ctx.beginPath();
    ctx.moveTo(0, 3);
    ctx.lineTo(0, arm);
    // Frost barbs.
    ctx.moveTo(0, arm * 0.62);
    ctx.lineTo(3, arm * 0.82);
    ctx.moveTo(0, arm * 0.62);
    ctx.lineTo(-3, arm * 0.82);
    ctx.stroke();
    ctx.restore();
  }
  // Hex core.
  const g = ctx.createRadialGradient(0, 0, 1, 0, 0, 7);
  g.addColorStop(0, '#ffffff');
  g.addColorStop(1, color);
  ctx.fillStyle = g;
  hexPath(ctx, 0, 0, 6.5, Math.PI / 6);
  ctx.fill();
  ctx.restore();
  glowDot(ctx, cx, cy, 2 + flash * 1.6, '#e8ffff', color);
}

/** The hexagonal mounting pad shared by all towers. */
function drawTowerPad(
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  color: string,
  selected: boolean,
  time: number,
): void {
  ctx.save();
  ctx.translate(cx, cy);
  const g = ctx.createRadialGradient(0, -4, 2, 0, 0, 17);
  g.addColorStop(0, '#16233f');
  g.addColorStop(1, INK.panel);
  ctx.fillStyle = g;
  hexPath(ctx, 0, 0, 16, -Math.PI / 2);
  ctx.fill();
  ctx.strokeStyle = withAlpha(color, 0.8);
  ctx.lineWidth = 1.4;
  ctx.stroke();
  if (selected) {
    ctx.rotate(time * 1.4);
    ctx.strokeStyle = color;
    ctx.lineWidth = 2;
    ctx.setLineDash([5, 5]);
    hexPath(ctx, 0, 0, 20, -Math.PI / 2);
    ctx.stroke();
    ctx.setLineDash([]);
  }
  ctx.restore();
}

/** Pulse Turret: a rotating barrel that tracks its target. */
function drawPulse(
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  tower: Tower,
  flash: number,
  time: number,
  enemyPos: Map<number, Pt>,
): void {
  const color = CONFIG.towers.pulse.color;
  const target = tower.targetId >= 0 ? enemyPos.get(tower.targetId) : undefined;
  const aim = target
    ? Math.atan2(target.y - cy, target.x - cx)
    : -Math.PI / 2 + Math.sin(time * 0.9 + tower.id) * 0.6;

  ctx.save();
  ctx.translate(cx, cy);
  ctx.rotate(aim);
  ctx.save();
  ctx.translate(-flash * 3, 0);
  ctx.fillStyle = color;
  roundRect(ctx, 2, -3.2, 15, 6.4, 2.4);
  ctx.fill();
  ctx.restore();
  ctx.restore();
  if (flash > 0) {
    const muzzle = { x: cx + Math.cos(aim) * 17, y: cy + Math.sin(aim) * 17 };
    glowDot(ctx, muzzle.x, muzzle.y, 1 + flash * 2.5, '#ffffff', color);
  }

  // Housing dome + glowing core.
  ctx.fillStyle = color;
  ctx.beginPath();
  ctx.arc(cx, cy, 8.5, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = '#06121f';
  ctx.beginPath();
  ctx.arc(cx, cy, 5, 0, Math.PI * 2);
  ctx.fill();
  glowDot(ctx, cx, cy, 2.4 + flash * 1.5, '#eafdff', color);
}

/**
 * Arc Coil: a charged triangular prism crackling with electricity. `charge` is
 * the live power-surplus coupling (docs §5), −1 (browned-out, demand outruns
 * supply) … +1 (a deeply over-built grid) — it brightens and multiplies the
 * crackle and wraps the coil in a surge halo, so a rich grid reads at a glance.
 */
function drawArc(
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  color: string,
  flash: number,
  time: number,
  charge: number,
): void {
  const chg = clamp(charge, -1, 1);
  ctx.save();
  ctx.translate(cx, cy);
  const tips: Pt[] = [];
  for (let i = 0; i < 3; i += 1) {
    const a = -Math.PI / 2 + (Math.PI * 2 * i) / 3;
    tips.push({ x: Math.cos(a) * 11, y: Math.sin(a) * 11 });
  }
  // Surge halo — a rich grid wraps the coil in a crackling power field; at a
  // neutral or browned-out grid there is none.
  if (chg > 0.001) {
    ctx.globalCompositeOperation = 'lighter';
    const haloPulse = 0.6 + 0.4 * Math.sin(time * 7);
    ctx.strokeStyle = withAlpha('#f0e0ff', 0.32 * chg * haloPulse);
    ctx.lineWidth = 1.4;
    ctx.beginPath();
    ctx.arc(0, 0, 15 + 4 * chg + 2 * haloPulse, 0, Math.PI * 2);
    ctx.stroke();
    ctx.globalCompositeOperation = 'source-over';
  }
  // Prism body.
  const g = ctx.createLinearGradient(0, -11, 0, 11);
  g.addColorStop(0, withAlpha(color, 0.95));
  g.addColorStop(1, withAlpha(color, 0.4));
  ctx.fillStyle = g;
  ctx.beginPath();
  tips.forEach((p, i) => (i === 0 ? ctx.moveTo(p.x, p.y) : ctx.lineTo(p.x, p.y)));
  ctx.closePath();
  ctx.fill();
  ctx.strokeStyle = color;
  ctx.lineWidth = 1.4;
  ctx.stroke();
  // Crackling arcs between the tips — brighter and wilder on a charged grid,
  // faint and calm in a brownout. A well-charged coil throws extra strands.
  ctx.globalCompositeOperation = 'lighter';
  ctx.strokeStyle = withAlpha('#f0e0ff', clamp(0.5 + flash * 0.5 + chg * 0.4, 0.12, 1));
  ctx.lineWidth = 1;
  const wob = (2 + flash * 3) * Math.max(0.3, 1 + 0.7 * chg);
  const strands = chg > 0.5 ? 6 : 3;
  for (let i = 0; i < strands; i += 1) {
    const a = tips[i % 3]!;
    const b = tips[(i + 1) % 3]!;
    const mx = (a.x + b.x) / 2 + Math.sin(time * 25 + i * 2) * wob;
    const my = (a.y + b.y) / 2 + Math.cos(time * 25 + i * 2) * wob;
    ctx.beginPath();
    ctx.moveTo(a.x, a.y);
    ctx.lineTo(mx, my);
    ctx.lineTo(b.x, b.y);
    ctx.stroke();
  }
  ctx.restore();
  glowDot(ctx, cx, cy + 1, 2.2 + flash * 1.6 + Math.max(0, chg) * 1.8, '#ffffff', color);
}

/** Pylon: a spinning diamond that radiates power into the grid. */
function drawPylon(
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  color: string,
  time: number,
): void {
  ctx.save();
  ctx.translate(cx, cy);
  // Power-supply aura, breathing outward.
  ctx.globalCompositeOperation = 'lighter';
  for (let i = 0; i < 2; i += 1) {
    const t = (time * 0.7 + i / 2) % 1;
    ctx.strokeStyle = withAlpha(color, 0.3 * (1 - t));
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    ctx.arc(0, 0, lerp(8, 20, t), 0, Math.PI * 2);
    ctx.stroke();
  }
  ctx.globalCompositeOperation = 'source-over';
  // Outer diamond.
  ctx.rotate(time * 0.8);
  ctx.strokeStyle = color;
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.moveTo(0, -12);
  ctx.lineTo(12, 0);
  ctx.lineTo(0, 12);
  ctx.lineTo(-12, 0);
  ctx.closePath();
  ctx.stroke();
  // Inner counter-spinning diamond.
  ctx.rotate(-time * 1.7);
  const g = ctx.createLinearGradient(0, -7, 0, 7);
  g.addColorStop(0, '#fff5c0');
  g.addColorStop(1, color);
  ctx.fillStyle = g;
  ctx.beginPath();
  ctx.moveTo(0, -7);
  ctx.lineTo(7, 0);
  ctx.lineTo(0, 7);
  ctx.lineTo(-7, 0);
  ctx.closePath();
  ctx.fill();
  ctx.restore();
}

/**
 * The Maze-Mode Wall — an inert structural slab that fills its tile so a
 * blocked route reads at a glance (docs/GAME_DESIGN.md §16.9). No glow, no
 * animation: it is deliberately the quietest object on the board, so the eye
 * goes to the guns and the enemies, not the masonry.
 */
function drawWall(
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  color: string,
  selected: boolean,
): void {
  ctx.save();
  const half = TS / 2 - 3;
  // Solid slab — a top-lit gradient gives it a little physical weight.
  const g = ctx.createLinearGradient(cx, cy - half, cx, cy + half);
  g.addColorStop(0, withAlpha(color, 0.96));
  g.addColorStop(1, withAlpha(color, 0.6));
  ctx.fillStyle = g;
  roundRect(ctx, cx - half, cy - half, half * 2, half * 2, 4);
  ctx.fill();
  // Brick seams (running bond) so it reads as masonry, never as a tower.
  ctx.strokeStyle = withAlpha('#0a0e1a', 0.5);
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(cx - half, cy);
  ctx.lineTo(cx + half, cy);
  ctx.moveTo(cx, cy - half);
  ctx.lineTo(cx, cy);
  ctx.moveTo(cx - half / 2, cy);
  ctx.lineTo(cx - half / 2, cy + half);
  ctx.moveTo(cx + half / 2, cy);
  ctx.lineTo(cx + half / 2, cy + half);
  ctx.stroke();
  // Edge — a bright accent ring when selected, a quiet self-coloured rim else.
  ctx.strokeStyle = selected ? C.accent : withAlpha(color, 0.92);
  ctx.lineWidth = selected ? 2 : 1.2;
  roundRect(ctx, cx - half, cy - half, half * 2, half * 2, 4);
  ctx.stroke();
  ctx.restore();
}

/** Splash Mortar: a stout trapezoid block with a stubby shell tube. */
function drawMortar(
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  tower: Tower,
  flash: number,
  time: number,
  enemyPos: Map<number, Pt>,
): void {
  const color = CONFIG.towers.mortar.color;
  const target = tower.targetId >= 0 ? enemyPos.get(tower.targetId) : undefined;
  // The tube tilts toward the target; idle, it sweeps gently skyward.
  const aim = target
    ? Math.atan2(target.y - cy, target.x - cx)
    : -Math.PI / 2 + Math.sin(time * 0.5 + tower.id) * 0.4;

  // Stout trapezoid body — the recognisable silhouette.
  ctx.save();
  ctx.translate(cx, cy);
  const g = ctx.createLinearGradient(0, -9, 0, 10);
  g.addColorStop(0, withAlpha(color, 0.97));
  g.addColorStop(1, withAlpha(color, 0.42));
  ctx.fillStyle = g;
  ctx.beginPath();
  ctx.moveTo(-7.5, -8);
  ctx.lineTo(7.5, -8);
  ctx.lineTo(12.5, 9);
  ctx.lineTo(-12.5, 9);
  ctx.closePath();
  ctx.fill();
  ctx.strokeStyle = color;
  ctx.lineWidth = 1.6;
  ctx.lineJoin = 'round';
  ctx.stroke();
  // Bolt studs along the base plate.
  ctx.fillStyle = '#1c0a04';
  for (const sx of [-8.5, 0, 8.5]) {
    ctx.beginPath();
    ctx.arc(sx, 5.5, 1.5, 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.restore();

  // Angled shell tube, recoiling on a shot.
  ctx.save();
  ctx.translate(cx, cy - 1);
  ctx.rotate(aim);
  ctx.translate(-flash * 2.6, 0);
  const bg = ctx.createLinearGradient(0, -4.5, 0, 4.5);
  bg.addColorStop(0, '#ffd6b8');
  bg.addColorStop(1, color);
  ctx.fillStyle = bg;
  roundRect(ctx, -4, -4.6, 16, 9.2, 3);
  ctx.fill();
  ctx.strokeStyle = withAlpha('#ffffff', 0.45);
  ctx.lineWidth = 1;
  ctx.stroke();
  // Muzzle ring.
  ctx.strokeStyle = '#ffe6cc';
  ctx.lineWidth = 1.6;
  ctx.beginPath();
  ctx.arc(12, 0, 3.8, 0, Math.PI * 2);
  ctx.stroke();
  ctx.restore();

  if (flash > 0) {
    const mx = cx + Math.cos(aim) * 14;
    const my = cy - 1 + Math.sin(aim) * 14;
    glowDot(ctx, mx, my, 1.5 + flash * 4, '#fff3d8', color);
  }
  glowDot(ctx, cx, cy + 1, 1.6 + flash * 1.4, '#ffe6cc', color);
}

/** Sniper Rail: a long narrow rail that tracks its target with a thin barrel. */
function drawSniper(
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  tower: Tower,
  flash: number,
  time: number,
  enemyPos: Map<number, Pt>,
): void {
  const color = CONFIG.towers.sniper.color;
  const target = tower.targetId >= 0 ? enemyPos.get(tower.targetId) : undefined;
  const aim = target
    ? Math.atan2(target.y - cy, target.x - cx)
    : -Math.PI / 2 + Math.sin(time * 0.6 + tower.id) * 0.5;

  ctx.save();
  ctx.translate(cx, cy);
  ctx.rotate(aim);
  ctx.save();
  // The long rail recoils sharply on a shot.
  ctx.translate(-flash * 4, 0);
  ctx.fillStyle = color;
  roundRect(ctx, 0, -2, 23, 4, 1.6);
  ctx.fill();
  ctx.fillStyle = '#ffe2f0';
  ctx.fillRect(3, -0.7, 17, 1.4);
  ctx.restore();
  ctx.restore();
  if (flash > 0) {
    const muzzle = { x: cx + Math.cos(aim) * 25, y: cy + Math.sin(aim) * 25 };
    glowDot(ctx, muzzle.x, muzzle.y, 1 + flash * 3.4, '#ffffff', color);
  }
  // Housing dome + dark scope core.
  ctx.fillStyle = color;
  ctx.beginPath();
  ctx.arc(cx, cy, 8, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = '#1a0712';
  ctx.beginPath();
  ctx.arc(cx, cy, 4.6, 0, Math.PI * 2);
  ctx.fill();
  glowDot(ctx, cx, cy, 2.2 + flash * 1.5, '#ffe2f0', color);
}

// --- enemies -----------------------------------------------------------------

/** Draw each living Glitch interpolated between ticks, with an HP bar. */
function drawEnemies(
  ctx: CanvasRenderingContext2D,
  state: GameState,
  enemyPos: Map<number, Pt>,
  time: number,
): void {
  for (const enemy of state.enemies) {
    if (!enemy.alive) continue;
    const pos = enemyPos.get(enemy.id);
    if (!pos) continue;
    // A healing Patch radiates its repair aura beneath the shape.
    if (enemy.type === 'patch') {
      const auraBorn = patchAura.get(enemy.id);
      if (auraBorn !== undefined) {
        drawPatchAura(ctx, pos.x, pos.y, (time - auraBorn) / PATCH_AURA_LIFE);
      }
    }
    const ahead = pointAtPath(enemy.path, Math.min(enemy.pathDist + 0.08, enemy.path.length - 1));
    const heading = Math.atan2(ahead.y + OY - pos.y, ahead.x - pos.x);
    // Green heal flash on a Glitch the repair field just topped up.
    const healBorn = healGlow.get(enemy.id);
    const heal = healBorn !== undefined ? clamp(1 - (time - healBorn) / HEAL_GLOW_LIFE, 0, 1) : 0;
    drawEnemyShape(ctx, enemy, pos.x, pos.y, heading, time, heal);
    const radius = enemyRadius(enemy.type);
    if (enemy.statuses.slow) {
      drawSlowAura(ctx, pos.x, pos.y, radius, time);
    }
    // Brittle (Cryo painter, §5) — a cracked-glass tint marks an enemy whose
    // next damage instance is amplified. Drawn over the slow aura when both
    // are active (a Cryo pulse always lands both), so the brittle cue is the
    // visible payoff for hitting the chokepoint.
    if (enemy.statuses.brittle) {
      drawBrittleAura(ctx, pos.x, pos.y, radius, enemy.statuses.brittle.amp, time);
    }
    // Corrosion is inert on unarmored Glitches — only armored ones show pitting.
    if (enemy.statuses.corrode && enemy.baseArmor > 0) {
      drawCorrodeOverlay(ctx, enemy, pos.x, pos.y, time);
    }
    // A Buffer's translucent shield bubble — thickness tracks shield / maxShield.
    if (enemy.maxShield > 0 && enemy.shield > 0) {
      const regen = enemy.shieldRegenCooldown === 0 && enemy.shield < enemy.maxShield;
      drawShieldBubble(ctx, pos.x, pos.y, radius, enemy.shield / enemy.maxShield, time, regen);
    }
    // An EMP'd Glitch crackles inside a white-blue static ring.
    if (enemy.statuses.emp) {
      drawEmpAura(ctx, pos.x, pos.y, radius, time);
    }
    drawHpBar(ctx, enemy, pos.x, pos.y);
  }
}

// --- patch heal tracking -----------------------------------------------------

/** Wall-clock lifetime of the green heal flash, seconds. */
const HEAL_GLOW_LIFE = 0.5;
/** Wall-clock lifetime of a Patch repair-aura pulse, seconds. */
const PATCH_AURA_LIFE = 0.8;

/** enemyId → wall-clock birth time of its green heal flash. */
let healGlow = new Map<number, number>();
/** patchId → wall-clock birth time of its repair-aura pulse. */
let patchAura = new Map<number, number>();
/** Last logical tick seen — detects heal-tick crossings and reset(). */
let healPrevTick = -1;

/**
 * Detect Patch heal pulses by watching the logical tick clock. A Patch repairs
 * nearby Glitches every `patchHealInterval` ticks (docs §5); on each crossing,
 * every Patch with company pulses a repair aura and the wounded Glitches in its
 * field flash green. Reads state only — never mutates it.
 */
function trackHeal(state: GameState, time: number): void {
  // A reset() drops the tick counter — clear stale effects so nothing lingers.
  if (state.tick < healPrevTick || state.tick === 0) {
    healGlow = new Map();
    patchAura = new Map();
  }
  const interval = CONFIG.patchHealInterval;
  const crossed =
    state.tick > 0 && Math.floor(state.tick / interval) !== Math.floor(healPrevTick / interval);
  healPrevTick = state.tick;

  if (crossed) {
    const radiusSq = (CONFIG.patchHealRadius * TS) ** 2;
    for (const patch of state.enemies) {
      if (!patch.alive || patch.type !== 'patch') continue;
      // An EMP'd Patch is stunned and skips its heal pulse — no aura.
      if (patch.statuses.emp) continue;
      const pp = pointAtPath(patch.path, patch.pathDist);
      let pulsed = false;
      for (const other of state.enemies) {
        if (other === patch || !other.alive) continue;
        const op = pointAtPath(other.path, other.pathDist);
        const dx = pp.x - op.x;
        const dy = pp.y - op.y;
        if (dx * dx + dy * dy > radiusSq) continue;
        pulsed = true;
        // Only Glitches actually missing HP get the green repair flash.
        if (other.hp < other.maxHp) healGlow.set(other.id, time);
      }
      if (pulsed) patchAura.set(patch.id, time);
    }
  }

  // Age out finished effects and ids that have left the field.
  const live = new Set<number>();
  for (const e of state.enemies) if (e.alive) live.add(e.id);
  for (const [id, born] of [...healGlow]) {
    if (time - born > HEAL_GLOW_LIFE || !live.has(id)) healGlow.delete(id);
  }
  for (const [id, born] of [...patchAura]) {
    if (time - born > PATCH_AURA_LIFE || !live.has(id)) patchAura.delete(id);
  }
}

/** A Patch's pulsing repair-aura ring — an expanding teal wavefront. */
function drawPatchAura(ctx: CanvasRenderingContext2D, x: number, y: number, p: number): void {
  const radius = CONFIG.patchHealRadius * TS;
  const fade = clamp(1 - p, 0, 1);
  const teal = C.patch;
  // Soft inner wash of repair light.
  glow(ctx, x, y, radius * 0.7, teal, 0.16 * fade);
  ctx.save();
  ctx.translate(x, y);
  ctx.globalCompositeOperation = 'lighter';
  // Expanding pulse wavefront.
  const rr = lerp(radius * 0.34, radius, clamp(p, 0, 1));
  ctx.strokeStyle = withAlpha(teal, 0.6 * fade);
  ctx.lineWidth = 2.6 * fade + 0.6;
  ctx.beginPath();
  ctx.arc(0, 0, rr, 0, Math.PI * 2);
  ctx.stroke();
  // Dashed boundary at the true heal radius.
  ctx.strokeStyle = withAlpha(teal, 0.42 * fade);
  ctx.lineWidth = 1.4;
  ctx.setLineDash([4, 5]);
  ctx.beginPath();
  ctx.arc(0, 0, radius, 0, Math.PI * 2);
  ctx.stroke();
  ctx.setLineDash([]);
  // Repair plus-marks riding the wavefront.
  ctx.strokeStyle = withAlpha('#eafff7', 0.75 * fade);
  ctx.lineWidth = 1.6;
  for (let i = 0; i < 6; i += 1) {
    const a = (Math.PI / 3) * i + p * 0.8;
    const px = Math.cos(a) * rr;
    const py = Math.sin(a) * rr;
    ctx.beginPath();
    ctx.moveTo(px - 3, py);
    ctx.lineTo(px + 3, py);
    ctx.moveTo(px, py - 3);
    ctx.lineTo(px, py + 3);
    ctx.stroke();
  }
  ctx.restore();
}

/** A pitted acid-green corrosion overlay + a row of stack pips (1–5). */
function drawCorrodeOverlay(
  ctx: CanvasRenderingContext2D,
  enemy: Enemy,
  x: number,
  y: number,
  time: number,
): void {
  const cor = enemy.statuses.corrode;
  if (!cor) return;
  const r = enemyRadius(enemy.type);
  const acid = C.corrode;
  // Sickly acid haze, denser with more stacks.
  glow(ctx, x, y, r * 1.9, acid, 0.18 + 0.1 * (cor.stacks / CONFIG.corrodeMaxStacks));

  ctx.save();
  ctx.translate(x, y);
  // Pitted blotches eating into the shell — deterministic per enemy, bubbling.
  const pits = 2 + cor.stacks;
  for (let i = 0; i < pits; i += 1) {
    const a = hash01(enemy.id * 17 + i) * Math.PI * 2;
    const rad = hash01(enemy.id * 31 + i) * r * 0.72;
    const bubble = 0.7 + 0.3 * Math.sin(time * 6 + i * 1.7 + enemy.id);
    const pr = (1.1 + hash01(enemy.id * 7 + i) * 1.7) * bubble;
    ctx.fillStyle = withAlpha(acid, 0.55);
    ctx.beginPath();
    ctx.arc(Math.cos(a) * rad, Math.sin(a) * rad, pr, 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.restore();

  // Stack pips — a row of acid diamonds beneath the enemy.
  const gap = 5;
  const pipY = y + r + 6;
  const x0 = x - (gap * (cor.stacks - 1)) / 2;
  for (let i = 0; i < cor.stacks; i += 1) {
    const px = x0 + i * gap;
    glow(ctx, px, pipY, 4, acid, 0.6);
    ctx.fillStyle = '#eaffc0';
    ctx.beginPath();
    ctx.moveTo(px, pipY - 2.2);
    ctx.lineTo(px + 2.2, pipY);
    ctx.lineTo(px, pipY + 2.2);
    ctx.lineTo(px - 2.2, pipY);
    ctx.closePath();
    ctx.fill();
  }
}

/**
 * A Buffer's translucent shield bubble — thickness / opacity ∝ shield fraction.
 * It flickers as the shield runs low; while `regen` a bright arc sweeps the
 * shell, reading as the shield knitting back together.
 */
function drawShieldBubble(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  r: number,
  frac: number,
  time: number,
  regen: boolean,
): void {
  // The bubble flickers as the shield runs low.
  const flick = frac < 0.4 ? 0.55 + 0.45 * Math.abs(Math.sin(time * 18)) : 1;
  const bubbleR = r + 4.5;
  ctx.save();
  // Faint inner wash.
  ctx.fillStyle = withAlpha(C.cryo, 0.1 * frac * flick);
  ctx.beginPath();
  ctx.arc(x, y, bubbleR, 0, Math.PI * 2);
  ctx.fill();
  // Bright shell — line weight tracks the shield fraction.
  ctx.globalCompositeOperation = 'lighter';
  ctx.strokeStyle = withAlpha('#cdeeff', (0.32 + 0.45 * frac) * flick);
  ctx.lineWidth = 1 + 2.6 * frac;
  ctx.beginPath();
  ctx.arc(x, y, bubbleR, 0, Math.PI * 2);
  ctx.stroke();
  // Hex facets riding the shell.
  ctx.strokeStyle = withAlpha('#eaf8ff', 0.5 * frac * flick);
  ctx.lineWidth = 1;
  for (let i = 0; i < 6; i += 1) {
    const a = (Math.PI / 3) * i + time * 0.5;
    const px = x + Math.cos(a) * bubbleR;
    const py = y + Math.sin(a) * bubbleR;
    ctx.beginPath();
    ctx.arc(px, py, 1.6, 0, Math.PI * 2);
    ctx.stroke();
  }
  // Regen: a bright arc sweeps the shell as the shield knits back in.
  if (regen) {
    const sweep = (time * 3.4) % (Math.PI * 2);
    ctx.strokeStyle = withAlpha('#eaf8ff', 0.85);
    ctx.lineWidth = 2.6;
    ctx.beginPath();
    ctx.arc(x, y, bubbleR, sweep, sweep + 0.7);
    ctx.stroke();
  }
  ctx.restore();
}

/** An EMP static ring — a white-blue cage of jittering arcs around a stunned enemy. */
function drawEmpAura(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  r: number,
  time: number,
): void {
  const col = C.emp;
  glow(ctx, x, y, r * 2.3, col, 0.4);
  ctx.save();
  ctx.translate(x, y);
  ctx.globalCompositeOperation = 'lighter';
  ctx.strokeStyle = withAlpha(col, 0.8);
  ctx.lineWidth = 1.3;
  ctx.beginPath();
  ctx.arc(0, 0, r + 4, 0, Math.PI * 2);
  ctx.stroke();
  // Crackling static spokes.
  ctx.lineWidth = 1.4;
  for (let i = 0; i < 5; i += 1) {
    const a = (Math.PI * 2 * i) / 5 + time * 2.4;
    const rr = r + 3 + Math.sin(time * 32 + i * 1.7) * 3;
    ctx.strokeStyle = withAlpha('#ffffff', 0.7);
    ctx.beginPath();
    ctx.moveTo(Math.cos(a) * (r - 2), Math.sin(a) * (r - 2));
    ctx.lineTo(Math.cos(a) * rr, Math.sin(a) * rr);
    ctx.stroke();
  }
  ctx.restore();
}

/** A frost shroud marking a slowed enemy — cold halo, crystal ring, rime. */
function drawSlowAura(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  r: number,
  time: number,
): void {
  const frost = C.frost;
  // Cold halo + a rime tint over the body.
  glow(ctx, x, y, r * 2.5, frost, 0.4);
  glow(ctx, x, y, r * 0.95, frost, 0.32);
  ctx.save();
  ctx.translate(x, y);
  // Slow-spinning crystalline ring with six frost nodes.
  ctx.rotate(time * 0.7);
  const ringR = r + 5;
  ctx.strokeStyle = withAlpha(frost, 0.7);
  ctx.lineWidth = 1.2;
  ctx.beginPath();
  ctx.arc(0, 0, ringR, 0, Math.PI * 2);
  ctx.stroke();
  for (let i = 0; i < 6; i += 1) {
    const a = (Math.PI / 3) * i;
    const px = Math.cos(a) * ringR;
    const py = Math.sin(a) * ringR;
    ctx.fillStyle = '#dffaff';
    ctx.beginPath();
    ctx.moveTo(px + Math.cos(a) * 2.6, py + Math.sin(a) * 2.6);
    ctx.lineTo(px + Math.cos(a + 2.1) * 2.2, py + Math.sin(a + 2.1) * 2.2);
    ctx.lineTo(px + Math.cos(a - 2.1) * 2.2, py + Math.sin(a - 2.1) * 2.2);
    ctx.closePath();
    ctx.fill();
  }
  ctx.restore();
}

/**
 * A cracked-glass overlay marking a Cryo-painted (brittle) enemy — frost
 * cracks radiating outward from the body, intensity scaling with the mark's
 * amp (×1.20 / ×1.30 / ×1.40 by Cryo level). Render-only — the damage uplift
 * is in `damage()`.
 */
function drawBrittleAura(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  r: number,
  amp: number,
  time: number,
): void {
  // Map amp ×1.20..×1.40 → intensity 0..1 (clamped) — visibly stronger crack
  // pattern at the L3 painter.
  const intensity = clamp((amp - 1.0) / 0.4, 0.3, 1.0);
  ctx.save();
  ctx.translate(x, y);
  // Faint cool-white outer halo so the brittle reads even on top of the slow
  // aura — both are Cryo-cyan, so the brittle tint pushes whiter.
  glow(ctx, 0, 0, r * 1.5, '#dffaff', 0.35 * intensity);
  // Slow-creeping crack pattern — eight radial spurs, alternating length,
  // gently rotating so the mark stays visibly *active* rather than a static
  // overlay. Stroke is white-ice; opacity scales with intensity.
  ctx.rotate(time * 0.18);
  ctx.strokeStyle = withAlpha('#e8feff', 0.55 + 0.3 * intensity);
  ctx.lineWidth = 1.0;
  ctx.lineCap = 'round';
  for (let i = 0; i < 8; i += 1) {
    const a = (Math.PI / 4) * i;
    const outer = r * (i % 2 === 0 ? 1.05 : 0.85);
    const inner = r * 0.45;
    ctx.beginPath();
    ctx.moveTo(Math.cos(a) * inner, Math.sin(a) * inner);
    // Spurs bend at a midpoint for the cracked-glass look.
    const midA = a + 0.18;
    const midR = (inner + outer) * 0.55;
    ctx.lineTo(Math.cos(midA) * midR, Math.sin(midA) * midR);
    ctx.lineTo(Math.cos(a) * outer, Math.sin(a) * outer);
    ctx.stroke();
  }
  ctx.restore();
}

// --- the Kernel boss (docs/GAME_DESIGN.md §17 / §18) -------------------------

/** Per-phase boss-bar hue — cyan → orange → red as the phases fall (§18.1.6). */
const KERNEL_BAR_COLOR: readonly string[] = ['', '#2de2e6', '#ff8c42', '#ff4d6d'];
/**
 * Per-phase i18n key suffixes for the Kernel HP-bar phase name. Index 0 is
 * unused (phase is 1-based). Resolved live through `t('boss.phaseName.' + …)`
 * so the boss reads in the player's language — Sprint 69 (i18n-gaps-extract-s69).
 */
const KERNEL_PHASE_KEY: readonly string[] = ['', 'boot', 'fork', 'panic'];
/** Wall-clock lifetime of the "⚠ KERNEL INCOMING" banner, seconds. */
const BOSS_BANNER_LIFE = 2.7;

/** Wall-clock birth of the active "⚠ KERNEL INCOMING" banner (−10 = inactive). */
let bossBannerBorn = -10;
/** Highest enemy id seen — anything above it that is a Kernel is freshly spawned. */
let bossMaxId = 0;
/** Run tick at the last track — a drop signals reset(), so tracking clears. */
let bossPrevTick = -1;

/**
 * Watch the roster for a freshly-spawned Kernel and arm the pre-spawn banner.
 * Entity ids are monotonic, so a high-water mark detects "new" in O(1) memory;
 * a `tick` / `nextId` drop signals a `reset()` and clears the latch.
 */
function trackBoss(state: GameState, time: number): void {
  if (state.tick < bossPrevTick || state.tick === 0 || state.nextId <= bossMaxId) {
    bossMaxId = 0;
    bossBannerBorn = -10;
  }
  bossPrevTick = state.tick;
  let maxId = bossMaxId;
  for (const e of state.enemies) {
    if (e.type === 'kernel' && e.id > bossMaxId) bossBannerBorn = time;
    if (e.id > maxId) maxId = e.id;
  }
  bossMaxId = maxId;
}

/**
 * The Kernel boss — a large, unstable glitch-mass whose silhouette shifts per
 * phase: Boot Sequence steady, Fork Bomb fracturing, Kernel Panic a violent
 * strobe (docs §11 / §17). A hot-crimson core inside a white-hot shell. The
 * Power-Drain telegraph + boss HP bar are drawn separately.
 */
function drawKernel(
  ctx: CanvasRenderingContext2D,
  enemy: Enemy,
  x: number,
  y: number,
  time: number,
): void {
  const r = enemyRadius(enemy.type);
  const phase = clamp(enemy.phase, 1, 3);
  const hit = enemy.hitFlash > 0;
  // Hue shifts calm crimson → orange → hot red; instability grows per phase.
  const hue = phase === 2 ? '#ff5630' : C.kernel;
  const shell = '#ffe8ec';
  const jitterAmp = (phase - 1) * 2.6 + (hit ? 3.4 : 0);
  // Kernel Panic violently strobes; Fork Bomb flickers; Boot Sequence is steady.
  const panicStrobe = phase >= 3 && Math.sin(time * 41 + enemy.id) > 0.4;
  const bright = hit || panicStrobe;

  // Outer aura — heavier with the phase and on a hit.
  glow(ctx, x, y, r * 3.1, hue, 0.46 + phase * 0.08 + (hit ? 0.32 : 0));
  if (bright) glow(ctx, x, y, r * 2.2, '#ffffff', 0.4);

  ctx.save();
  ctx.translate(x, y);

  // --- corrupting outer mass — a jittering many-sided polygon ---
  const sides = 11;
  const spin = time * (0.22 + phase * 0.14);
  const verts: Pt[] = [];
  for (let i = 0; i < sides; i += 1) {
    const a = (Math.PI * 2 * i) / sides + spin;
    const wob = 1 + Math.sin(time * (5 + phase * 2) + i * 2.1) * (0.05 + phase * 0.045);
    const j = (hash01(enemy.id * 13 + i + Math.floor(time * (7 + phase * 16))) - 0.5) * jitterAmp;
    const rr = r * wob + j;
    verts.push({ x: Math.cos(a) * rr, y: Math.sin(a) * rr });
  }
  ctx.beginPath();
  verts.forEach((p, i) => (i === 0 ? ctx.moveTo(p.x, p.y) : ctx.lineTo(p.x, p.y)));
  ctx.closePath();
  const body = ctx.createRadialGradient(0, 0, 2, 0, 0, r * 1.06);
  body.addColorStop(0, bright ? '#ffffff' : shell);
  body.addColorStop(0.5, bright ? withAlpha(shell, 0.96) : withAlpha(hue, 0.82));
  body.addColorStop(1, withAlpha(hue, 0.4));
  ctx.fillStyle = body;
  ctx.fill();
  ctx.strokeStyle = bright ? '#ffffff' : shell;
  ctx.lineWidth = 2.3;
  ctx.lineJoin = 'round';
  ctx.stroke();

  // --- phase 1: a calm boot-scanline sweep inside the shell ---
  if (phase === 1) {
    ctx.save();
    ctx.beginPath();
    ctx.arc(0, 0, r * 0.9, 0, Math.PI * 2);
    ctx.clip();
    ctx.strokeStyle = withAlpha('#ffffff', 0.42);
    ctx.lineWidth = 2;
    for (let i = 0; i < 3; i += 1) {
      const sy = ((time * 24 + i * r * 0.66) % (r * 2)) - r;
      ctx.beginPath();
      ctx.moveTo(-r, sy);
      ctx.lineTo(r, sy);
      ctx.stroke();
    }
    ctx.restore();
  }

  // --- phase 2+: fracture cracks splitting the mass ---
  if (phase >= 2) {
    ctx.strokeStyle = withAlpha(bright ? '#ffffff' : hue, 0.9);
    ctx.lineWidth = 1.7;
    ctx.lineCap = 'round';
    for (let i = 0; i < 5; i += 1) {
      const a = hash01(enemy.id * 7 + i) * Math.PI * 2;
      const wob = Math.sin(time * 6 + i) * 3;
      ctx.beginPath();
      ctx.moveTo(wob * 0.3, -wob * 0.3);
      ctx.lineTo(Math.cos(a) * r * 0.92 + wob, Math.sin(a) * r * 0.92 - wob);
      ctx.stroke();
    }
  }

  // --- phase 3: glitch shards tearing across the body ---
  if (phase >= 3) {
    for (let i = 0; i < 6; i += 1) {
      const t2 = Math.floor(time * 24) + i * 7;
      const gx = (hash01(enemy.id * 19 + t2) - 0.5) * r * 2.1;
      const gy = (hash01(enemy.id * 29 + t2) - 0.5) * r * 2.1;
      const gw = 4 + hash01(enemy.id * 5 + t2) * r * 0.8;
      ctx.fillStyle = withAlpha(hash01(t2) < 0.5 ? '#ffffff' : hue, 0.72);
      ctx.fillRect(gx, gy, gw, 2.6);
    }
  }

  // --- white-hot unstable core ---
  const coreR = r * 0.4 * (1 + 0.15 * Math.sin(time * (8 + phase * 3)));
  const cg = ctx.createRadialGradient(0, 0, 1, 0, 0, coreR);
  cg.addColorStop(0, '#ffffff');
  cg.addColorStop(0.62, withAlpha(shell, 0.96));
  cg.addColorStop(1, withAlpha(hue, 0.5));
  ctx.fillStyle = cg;
  ctx.beginPath();
  ctx.arc(0, 0, coreR, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();

  // --- phase 2+: detached fork shards orbiting the boss ---
  if (phase >= 2) {
    const shards = phase >= 3 ? 5 : 3;
    for (let i = 0; i < shards; i += 1) {
      const a = time * (0.5 + phase * 0.18) * (i % 2 ? 1 : -1) + (Math.PI * 2 * i) / shards;
      const dist = r * 1.12 + Math.sin(time * 4 + i) * 4 + (phase - 2) * 7;
      const sx = x + Math.cos(a) * dist;
      const sy = y + Math.sin(a) * dist;
      const sr = 4 + hash01(enemy.id * 3 + i) * 3;
      glow(ctx, sx, sy, sr * 2.6, hue, 0.5);
      ctx.fillStyle = bright ? '#ffffff' : shell;
      ctx.beginPath();
      ctx.arc(sx, sy, sr, 0, Math.PI * 2);
      ctx.fill();
    }
  }
  glowDot(ctx, x, y, 3.4, '#ffffff', hue);
}

/**
 * The Kernel's Power-Drain telegraph + active-drain siphon (docs §17.3). While
 * `drainCharge > 0` a charge ring tightens onto the boss — the visible 2.5 s
 * wind-up; while `drainTicksLeft > 0` an orange siphon vortex spins around it.
 * Both are driven straight off the live boss-internal counters.
 */
function drawBossDrainFx(
  ctx: CanvasRenderingContext2D,
  state: GameState,
  enemyPos: Map<number, Pt>,
  time: number,
): void {
  if (state.drainCharge <= 0 && state.drainTicksLeft <= 0) return;
  for (const e of state.enemies) {
    if (e.type !== 'kernel') continue;
    const pos = enemyPos.get(e.id);
    if (!pos) continue;
    const r = enemyRadius('kernel');
    if (state.drainTicksLeft > 0) drawDrainActive(ctx, pos.x, pos.y, r, time);
    if (state.drainCharge > 0) {
      // Charge progress 0 → 1 as the telegraph counts down to the emit.
      const prog = clamp(1 - state.drainCharge / Math.max(1, CONFIG.bossDrainTelegraph), 0, 1);
      drawDrainCharge(ctx, pos.x, pos.y, r, prog, time);
    }
  }
}

/** The tightening Power-Drain charge ring — a telegraphed wind-up (§17.3). */
function drawDrainCharge(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  r: number,
  prog: number,
  time: number,
): void {
  const col = C.warning;
  const ringR = r * lerp(3.4, 1.16, prog);
  const pulse = 0.5 + 0.5 * Math.sin(time * (7 + prog * 26));
  ctx.save();
  ctx.translate(x, y);
  ctx.globalCompositeOperation = 'lighter';
  // Energy motes converging onto the ring.
  for (let i = 0; i < 10; i += 1) {
    const a = (Math.PI * 2 * i) / 10 + time * 0.9;
    const mr = ringR + 5 + Math.sin(time * 6 + i) * 4;
    glow(ctx, Math.cos(a) * mr, Math.sin(a) * mr, 5, col, 0.32 + 0.4 * prog);
  }
  // The contracting charge ring.
  ctx.strokeStyle = withAlpha(col, 0.4 + 0.55 * prog * pulse);
  ctx.lineWidth = 2 + 3.4 * prog;
  ctx.setLineDash([7, 7]);
  ctx.lineDashOffset = -time * 44;
  ctx.beginPath();
  ctx.arc(0, 0, ringR, 0, Math.PI * 2);
  ctx.stroke();
  ctx.setLineDash([]);
  ctx.strokeStyle = withAlpha('#ffffff', 0.25 + 0.55 * prog);
  ctx.lineWidth = 1.4;
  ctx.beginPath();
  ctx.arc(0, 0, ringR * 0.95, 0, Math.PI * 2);
  ctx.stroke();
  ctx.restore();
  // A "⚡ POWER DRAIN" warning tag above the boss as the charge nears emit.
  if (prog > 0.18) {
    const label = t('boss.powerDrain');
    ctx.save();
    ctx.font = `700 10px ${FONT_MONO}`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    const w = ctx.measureText(label).width + 16;
    const ty = y - r - 20;
    ctx.fillStyle = 'rgba(10, 7, 3, 0.82)';
    roundRect(ctx, x - w / 2, ty - 9, w, 18, 9);
    ctx.fill();
    ctx.fillStyle = withAlpha(col, 0.6 + 0.4 * pulse);
    ctx.fillText(label, x, ty + 1);
    ctx.restore();
  }
}

/** The active Power-Drain siphon — an orange vortex spiralling off the boss. */
function drawDrainActive(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  r: number,
  time: number,
): void {
  const col = C.warning;
  ctx.save();
  ctx.translate(x, y);
  ctx.globalCompositeOperation = 'lighter';
  glow(ctx, 0, 0, r * 2.7, col, 0.34 + 0.18 * Math.sin(time * 7));
  for (let i = 0; i < 4; i += 1) {
    const base = time * 3.2 + (Math.PI * 2 * i) / 4;
    ctx.strokeStyle = withAlpha(col, 0.62);
    ctx.lineWidth = 2;
    ctx.beginPath();
    for (let s = 0; s <= 12; s += 1) {
      const t = s / 12;
      const ang = base + t * 2.3;
      const rad = r * 1.05 + t * r * 1.7;
      const px = Math.cos(ang) * rad;
      const py = Math.sin(ang) * rad;
      if (s === 0) ctx.moveTo(px, py);
      else ctx.lineTo(px, py);
    }
    ctx.stroke();
  }
  ctx.restore();
}

/**
 * Boss-fight UI — the dedicated HP bar pinned below the HUD strip while a
 * Kernel is alive, plus the flashing "⚠ KERNEL INCOMING" pre-spawn banner.
 * Drawn outside the screen-shake transform so it stays steady (§18.1.6).
 */
function drawBossUi(ctx: CanvasRenderingContext2D, state: GameState, time: number): void {
  let boss: Enemy | null = null;
  for (const e of state.enemies) {
    if (e.type === 'kernel') {
      boss = e;
      break;
    }
  }
  if (boss) drawBossHpBar(ctx, boss, time);
  const bannerAge = time - bossBannerBorn;
  if (bannerAge >= 0 && bannerAge < BOSS_BANNER_LIFE) drawKernelIncoming(ctx, bannerAge);
}

/**
 * The dedicated Kernel HP bar — pinned below the HUD strip, labelled "THE
 * KERNEL", segmented at the 60 % / 25 % phase thresholds and colour-shifting
 * cyan → orange → red as the phases fall (docs §11 / §18.1.6).
 */
function drawBossHpBar(ctx: CanvasRenderingContext2D, boss: Enemy, time: number): void {
  const phase = clamp(boss.phase, 1, 3);
  const col = KERNEL_BAR_COLOR[phase] ?? C.bad;
  const frac = clamp(boss.hp / Math.max(1, boss.maxHp), 0, 1);
  const barW = 540;
  const barH = 15;
  const px = (PW - barW) / 2;
  const py = OY + 30;
  const pulse = 0.5 + 0.5 * Math.sin(time * 3);

  ctx.save();
  // Backing panel.
  const panelX = px - 14;
  const panelY = OY + 6;
  const panelW = barW + 28;
  const panelH = 46;
  const g = ctx.createLinearGradient(0, panelY, 0, panelY + panelH);
  g.addColorStop(0, 'rgba(16, 10, 16, 0.93)');
  g.addColorStop(1, 'rgba(9, 7, 12, 0.93)');
  ctx.fillStyle = g;
  roundRect(ctx, panelX, panelY, panelW, panelH, 9);
  ctx.fill();
  ctx.strokeStyle = withAlpha(col, 0.6 + 0.3 * pulse);
  ctx.lineWidth = 1.4;
  ctx.shadowBlur = 12 + 6 * pulse;
  ctx.shadowColor = col;
  roundRect(ctx, panelX, panelY, panelW, panelH, 9);
  ctx.stroke();
  ctx.shadowBlur = 0;

  // Header — "⚠ THE KERNEL" left, the live phase name right.
  ctx.textBaseline = 'alphabetic';
  ctx.textAlign = 'left';
  ctx.font = `700 12px ${FONT_DISPLAY}`;
  ctx.fillStyle = col;
  ctx.shadowBlur = 7;
  ctx.shadowColor = col;
  ctx.fillText(t('boss.kernelHeader'), px, py - 6);
  ctx.shadowBlur = 0;
  ctx.textAlign = 'right';
  ctx.font = `9px ${FONT_MONO}`;
  ctx.fillStyle = C.hudMuted;
  const phaseKey = KERNEL_PHASE_KEY[phase];
  const phaseName = phaseKey ? t(`boss.phaseName.${phaseKey}`) : '';
  ctx.fillText(t('boss.phase', { phase, name: phaseName }), px + barW, py - 6);

  // Bar housing.
  ctx.fillStyle = '#06101f';
  roundRect(ctx, px, py, barW, barH, 3);
  ctx.fill();

  // HP fill — a phase-coloured gradient with a bright leading edge.
  const fillW = Math.max(2, barW * frac);
  const fg = ctx.createLinearGradient(px, 0, px + barW, 0);
  fg.addColorStop(0, withAlpha(col, 0.7));
  fg.addColorStop(1, withAlpha(col, 1));
  ctx.fillStyle = fg;
  roundRect(ctx, px, py, fillW, barH, 3);
  ctx.fill();
  ctx.fillStyle = withAlpha('#ffffff', 0.55);
  ctx.fillRect(px + fillW - 2.5, py, 2.5, barH);
  // Inner segment ticks for a diagnostics look.
  ctx.strokeStyle = 'rgba(6, 10, 20, 0.55)';
  ctx.lineWidth = 1;
  for (let i = 1; i < 20; i += 1) {
    const sx = px + (barW * i) / 20;
    ctx.beginPath();
    ctx.moveTo(sx, py + 1);
    ctx.lineTo(sx, py + barH - 1);
    ctx.stroke();
  }

  // Phase-threshold notches at 60 % and 25 % HP.
  for (const thr of [CONFIG.bossPhase2HpFrac, CONFIG.bossPhase3HpFrac]) {
    const nx = px + barW * thr;
    ctx.strokeStyle = withAlpha('#ffe8ec', frac > thr ? 0.92 : 0.32);
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(nx, py - 3);
    ctx.lineTo(nx, py + barH + 3);
    ctx.stroke();
  }

  // Bar frame.
  ctx.strokeStyle = withAlpha(col, 0.7);
  ctx.lineWidth = 1.2;
  roundRect(ctx, px, py, barW, barH, 3);
  ctx.stroke();
  ctx.restore();
}

/**
 * The "⚠ KERNEL INCOMING" pre-spawn banner — a flashing hazard alert that
 * telegraphs the boss (docs §17.4). Fades in and out around a flashing core.
 */
function drawKernelIncoming(ctx: CanvasRenderingContext2D, age: number): void {
  const p = age / BOSS_BANNER_LIFE;
  // Fade in fast, hold, fade out.
  const fade = clamp(Math.min(p / 0.12, (1 - p) / 0.22), 0, 1);
  const flash = 0.55 + 0.45 * Math.sin(age * 17);
  const cx = PW / 2;
  const cy = OY + PH * 0.34;
  const text = t('boss.kernelIncoming');

  ctx.save();
  ctx.globalAlpha = fade;
  ctx.font = `700 30px ${FONT_DISPLAY}`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  const tw = ctx.measureText(text).width;
  const bw = tw + 80;
  const bh = 58;

  // Hazard-striped backing bar.
  ctx.save();
  roundRect(ctx, cx - bw / 2, cy - bh / 2, bw, bh, 8);
  ctx.clip();
  ctx.fillStyle = 'rgba(12, 6, 4, 0.92)';
  ctx.fillRect(cx - bw / 2, cy - bh / 2, bw, bh);
  ctx.globalAlpha = fade * 0.16;
  ctx.fillStyle = C.warning;
  for (let i = -bh; i < bw + bh; i += 26) {
    ctx.beginPath();
    ctx.moveTo(cx - bw / 2 + i, cy - bh / 2);
    ctx.lineTo(cx - bw / 2 + i + 13, cy - bh / 2);
    ctx.lineTo(cx - bw / 2 + i + 13 - bh, cy + bh / 2);
    ctx.lineTo(cx - bw / 2 + i - bh, cy + bh / 2);
    ctx.closePath();
    ctx.fill();
  }
  ctx.restore();

  // Frame.
  ctx.strokeStyle = withAlpha(C.bad, 0.5 + 0.5 * flash);
  ctx.lineWidth = 2.4;
  ctx.shadowBlur = 16 * flash;
  ctx.shadowColor = C.bad;
  roundRect(ctx, cx - bw / 2, cy - bh / 2, bw, bh, 8);
  ctx.stroke();
  ctx.shadowBlur = 0;

  // Headline text.
  ctx.fillStyle = withAlpha('#05070f', 0.8);
  ctx.fillText(text, cx + 2, cy + 2);
  ctx.fillStyle = withAlpha('#ffe8ec', 0.7 + 0.3 * flash);
  ctx.shadowBlur = 12 * flash;
  ctx.shadowColor = C.bad;
  ctx.fillText(text, cx, cy);
  ctx.restore();
}

/** A type-specific neon Glitch. `heal` (0..1) drives the green repair flash. */
function drawEnemyShape(
  ctx: CanvasRenderingContext2D,
  enemy: Enemy,
  x: number,
  y: number,
  heading: number,
  time: number,
  heal: number,
): void {
  // The Kernel boss has its own draw — and no `config.enemies` roster entry.
  if (enemy.type === 'kernel') {
    drawKernel(ctx, enemy, x, y, time);
    return;
  }
  const def = CONFIG.enemies[enemy.type];
  const r = def.radius;
  const hit = enemy.hitFlash > 0;
  const fill = hit ? '#ffffff' : def.color;

  glow(ctx, x, y, hit ? r * 4 : r * 2.6, def.color, hit ? 0.95 : 0.5);
  // A Glitch the Patch field just repaired flashes green.
  if (heal > 0) glow(ctx, x, y, r * 3.6, C.good, 0.7 * heal);

  ctx.save();
  ctx.translate(x, y);
  ctx.fillStyle = fill;
  ctx.strokeStyle = hit ? '#ffffff' : withAlpha(def.color, 0.9);
  ctx.lineWidth = 1.5;

  if (enemy.type === 'bit') {
    ctx.beginPath();
    ctx.arc(0, 0, r, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = '#06121f';
    ctx.beginPath();
    ctx.arc(0, 0, r * 0.42, 0, Math.PI * 2);
    ctx.fill();
  } else if (enemy.type === 'packet') {
    ctx.rotate(heading);
    ctx.beginPath();
    ctx.moveTo(r * 1.25, 0);
    ctx.lineTo(0, r * 0.85);
    ctx.lineTo(-r * 0.8, 0);
    ctx.lineTo(0, -r * 0.85);
    ctx.closePath();
    ctx.fill();
    ctx.stroke();
  } else if (enemy.type === 'daemon') {
    ctx.rotate(time * 0.6);
    hexPath(ctx, 0, 0, r, 0);
    ctx.fill();
    ctx.stroke();
    ctx.rotate(-time * 1.4);
    ctx.fillStyle = '#06121f';
    hexPath(ctx, 0, 0, r * 0.6, 0);
    ctx.fill();
    ctx.fillStyle = hit ? '#fff' : withAlpha(def.color, 0.85);
    ctx.beginPath();
    ctx.arc(0, 0, r * 0.26 + Math.sin(time * 6) * 1.2, 0, Math.PI * 2);
    ctx.fill();
  } else if (enemy.type === 'fork') {
    // Fork — twin violet lobes straining to split, with a forked tail.
    ctx.rotate(heading);
    const split = r * 0.42 * (1 + 0.2 * Math.sin(time * 5 + enemy.id));
    // Forked tail prongs trailing behind.
    ctx.strokeStyle = hit ? '#ffffff' : withAlpha(def.color, 0.9);
    ctx.lineCap = 'round';
    ctx.lineWidth = 2;
    for (const s of [-1, 1]) {
      ctx.beginPath();
      ctx.moveTo(-r * 0.2, s * split * 0.5);
      ctx.lineTo(-r * 1.2, s * split * 1.35);
      ctx.stroke();
    }
    // Twin lobes.
    for (const s of [-1, 1]) {
      ctx.fillStyle = fill;
      ctx.beginPath();
      ctx.arc(r * 0.12, s * split, r * 0.72, 0, Math.PI * 2);
      ctx.fill();
      ctx.stroke();
    }
    // Bright dividing seam where the lobes meet + a nucleus in each lobe.
    ctx.strokeStyle = hit ? '#ffffff' : '#f0d6ff';
    ctx.lineWidth = 1.4;
    ctx.beginPath();
    ctx.moveTo(r * 0.12 - r * 0.66, 0);
    ctx.lineTo(r * 0.12 + r * 0.66, 0);
    ctx.stroke();
    ctx.fillStyle = hit ? '#ffffff' : '#1a0e2e';
    for (const s of [-1, 1]) {
      ctx.beginPath();
      ctx.arc(r * 0.12, s * split, r * 0.28, 0, Math.PI * 2);
      ctx.fill();
    }
  } else if (enemy.type === 'cipher') {
    // Cipher — a steel-blue plated octagon whose armor shell cracks apart as
    // Corrosion shreds it (intact plates ∝ effective armor / base armor).
    ctx.rotate(time * 0.25);
    const armorFrac = enemy.baseArmor > 0 ? clamp(enemy.armor / enemy.baseArmor, 0, 1) : 0;
    // Core octagon.
    const og = ctx.createRadialGradient(0, 0, 2, 0, 0, r);
    og.addColorStop(0, hit ? '#ffffff' : '#aac6ff');
    og.addColorStop(1, hit ? '#ffffff' : def.color);
    ctx.fillStyle = og;
    octagonPath(ctx, r * 0.72);
    ctx.fill();
    ctx.stroke();
    // Centre bolt.
    ctx.fillStyle = hit ? '#ffffff' : '#0c1430';
    ctx.beginPath();
    ctx.arc(0, 0, r * 0.2, 0, Math.PI * 2);
    ctx.fill();
    // Armor shell — eight plates; shed plates leave a gap + a crack stub.
    const plates = 8;
    const intact = Math.round(plates * armorFrac);
    const shellR = r + 2.6;
    const seg = (Math.PI * 2) / plates;
    for (let i = 0; i < plates; i += 1) {
      const a0 = seg * i - Math.PI / 8;
      const lost = CIPHER_PLATE_ORDER.indexOf(i) >= intact;
      if (!lost) {
        ctx.strokeStyle = hit ? '#ffffff' : '#c4d8ff';
        ctx.lineWidth = 2.8;
        ctx.beginPath();
        ctx.arc(0, 0, shellR, a0, a0 + seg * 0.72);
        ctx.stroke();
      } else {
        ctx.strokeStyle = withAlpha(def.color, 0.3);
        ctx.lineWidth = 1.3;
        ctx.beginPath();
        ctx.arc(0, 0, shellR, a0, a0 + seg * 0.24);
        ctx.stroke();
      }
    }
    // Fault cracks across the core deepen as plates are lost.
    ctx.strokeStyle = hit ? '#ffffff' : withAlpha('#0c1430', 0.85);
    ctx.lineWidth = 1.2;
    const cracks = Math.min(plates - intact, 4);
    for (let i = 0; i < cracks; i += 1) {
      const a = CIPHER_PLATE_ORDER[i]! * seg + 0.4;
      ctx.beginPath();
      ctx.moveTo(0, 0);
      ctx.lineTo(Math.cos(a) * r * 0.66, Math.sin(a) * r * 0.66);
      ctx.stroke();
    }
  } else if (enemy.type === 'patch') {
    // Patch — a teal repair cross; the healer of the Glitch roster.
    const pulse = 0.86 + 0.14 * Math.sin(time * 4 + enemy.id);
    const arm = r * 0.98 * pulse;
    const thick = r * 0.6;
    const radius = thick * 0.34;
    ctx.lineWidth = 1.5;
    // Two rounded bars crossing into a plus.
    ctx.fillStyle = fill;
    roundRect(ctx, -thick / 2, -arm, thick, arm * 2, radius);
    ctx.fill();
    ctx.stroke();
    roundRect(ctx, -arm, -thick / 2, arm * 2, thick, radius);
    ctx.fill();
    ctx.stroke();
    // Bright repair node at the cross.
    const ng = ctx.createRadialGradient(0, 0, 0.5, 0, 0, r * 0.42);
    ng.addColorStop(0, '#ffffff');
    ng.addColorStop(1, hit ? '#ffffff' : def.color);
    ctx.fillStyle = ng;
    ctx.beginPath();
    ctx.arc(0, 0, r * 0.34, 0, Math.PI * 2);
    ctx.fill();
  } else if (enemy.type === 'streak') {
    // Sprint 31 — Streak: an electric-magenta arrow / chevron pointing along
    // the path tangent (docs/ENEMY_DESIGN.md §3.1). When the lone-wolf sprint
    // is active and `juiceEnabled`, a 12 px motion trail line is drawn
    // *behind* the chevron — the visual cue that this Streak has out-paced
    // its escort. Both effects are pure-additive render (the sim never
    // reads them).
    ctx.rotate(heading);
    if (enemy.isSprinting && CONFIG.juiceEnabled) {
      ctx.save();
      ctx.globalCompositeOperation = 'lighter';
      ctx.strokeStyle = withAlpha(def.color, 0.65);
      ctx.lineWidth = 2.2;
      ctx.lineCap = 'round';
      ctx.beginPath();
      ctx.moveTo(-r * 0.8, 0);
      ctx.lineTo(-r * 2.5, 0);
      ctx.stroke();
      ctx.restore();
    }
    // Sharp arrow body — a slim chevron, brighter tip.
    ctx.beginPath();
    ctx.moveTo(r * 1.35, 0);
    ctx.lineTo(-r * 0.35, r * 0.85);
    ctx.lineTo(-r * 0.05, 0);
    ctx.lineTo(-r * 0.35, -r * 0.85);
    ctx.closePath();
    ctx.fill();
    ctx.stroke();
    // Trailing wedge that suggests forward motion.
    ctx.fillStyle = hit ? '#ffffff' : withAlpha('#ffd1f0', 0.95);
    ctx.beginPath();
    ctx.moveTo(r * 1.05, 0);
    ctx.lineTo(r * 0.1, r * 0.35);
    ctx.lineTo(r * 0.1, -r * 0.35);
    ctx.closePath();
    ctx.fill();
  } else {
    // Buffer — a sky-blue octagon; its regenerating shield bubble is drawn
    // separately (see drawShieldBubble).
    ctx.rotate(time * 0.3);
    const og = ctx.createRadialGradient(0, 0, 2, 0, 0, r);
    og.addColorStop(0, hit ? '#ffffff' : '#bfeaff');
    og.addColorStop(1, hit ? '#ffffff' : def.color);
    ctx.fillStyle = og;
    octagonPath(ctx, r * 0.82);
    ctx.fill();
    ctx.stroke();
    // Dark inner ring with a bright nucleus.
    ctx.fillStyle = hit ? '#ffffff' : '#0a2233';
    octagonPath(ctx, r * 0.44);
    ctx.fill();
    ctx.fillStyle = hit ? '#ffffff' : '#bfeaff';
    ctx.beginPath();
    ctx.arc(0, 0, r * 0.2, 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.restore();

  // Green repair ring on a freshly-healed Glitch.
  if (heal > 0) {
    ctx.save();
    ctx.globalCompositeOperation = 'lighter';
    ctx.strokeStyle = withAlpha(C.good, 0.85 * heal);
    ctx.lineWidth = 1.6;
    ctx.beginPath();
    ctx.arc(x, y, r + 3 + (1 - heal) * 7, 0, Math.PI * 2);
    ctx.stroke();
    ctx.restore();
  }
}

/** Trace a regular octagon subpath centred at the current origin. */
function octagonPath(ctx: CanvasRenderingContext2D, r: number): void {
  ctx.beginPath();
  for (let i = 0; i < 8; i += 1) {
    const a = (Math.PI / 4) * i + Math.PI / 8;
    const px = Math.cos(a) * r;
    const py = Math.sin(a) * r;
    if (i === 0) ctx.moveTo(px, py);
    else ctx.lineTo(px, py);
  }
  ctx.closePath();
}

/** A slim neon HP bar, hidden for full-health chaff. */
function drawHpBar(ctx: CanvasRenderingContext2D, enemy: Enemy, x: number, y: number): void {
  // The Kernel boss has its own dedicated HP bar pinned below the HUD (§18.1.6).
  if (enemy.type === 'kernel') return;
  const r = enemyRadius(enemy.type);
  const frac = clamp(enemy.hp / enemy.maxHp, 0, 1);
  // Chaff hides its bar at full health; tougher archetypes always show one.
  if (
    frac >= 1 &&
    enemy.type !== 'daemon' &&
    enemy.type !== 'fork' &&
    enemy.type !== 'buffer'
  ) {
    return;
  }
  const w = r * 2 + 6;
  const h = 3.5;
  const top = y - r - 9;
  const left = x - w / 2;
  ctx.fillStyle = 'rgba(4, 8, 18, 0.9)';
  roundRect(ctx, left - 1, top - 1, w + 2, h + 2, 2);
  ctx.fill();
  const col = frac > 0.5 ? C.good : frac > 0.25 ? C.warning : C.bad;
  ctx.fillStyle = col;
  roundRect(ctx, left, top, Math.max(0.5, w * frac), h, 1.5);
  ctx.fill();
}

// --- beams + particles -------------------------------------------------------

/** Draw active hitscan beam effects. */
function drawProjectiles(ctx: CanvasRenderingContext2D, state: GameState): void {
  if (state.projectiles.length === 0) return;
  ctx.save();
  ctx.translate(0, OY);
  ctx.lineCap = 'round';
  ctx.globalCompositeOperation = 'lighter';
  for (const p of state.projectiles) {
    // The Airstrike shell + blast ring are drawn entirely by drawVfx.
    if (p.kind === 'airstrike') continue;
    const color = CONFIG.towers[p.kind].color;
    const alpha = clamp(p.ttlTicks / CONFIG.beamTtlTicks, 0, 1);
    if (p.kind === 'arc') drawArcBeam(ctx, p.from, p.to, color, alpha, p.id);
    else if (p.kind === 'cryo') drawCryoLink(ctx, p.from, p.to, color, alpha);
    else if (p.kind === 'mortar') continue; // shell + blast drawn by drawVfx
    else if (p.kind === 'sniper') drawSniperBeam(ctx, p.from, p.to, color, alpha);
    else drawPulseBeam(ctx, p.from, p.to, color, alpha);
  }
  ctx.globalCompositeOperation = 'source-over';
  ctx.restore();
}

/** A Cryo Emitter chill link: a faint frost thread ending in an ice sparkle. */
function drawCryoLink(
  ctx: CanvasRenderingContext2D,
  from: Pt,
  to: Pt,
  color: string,
  alpha: number,
): void {
  ctx.strokeStyle = withAlpha(color, alpha * 0.3);
  ctx.lineWidth = 1.4;
  ctx.beginPath();
  ctx.moveTo(from.x, from.y);
  ctx.lineTo(to.x, to.y);
  ctx.stroke();
  // Ice sparkle on the chilled enemy.
  ctx.strokeStyle = withAlpha('#e8ffff', alpha);
  ctx.lineWidth = 1.4;
  const s = 4 + alpha * 2;
  for (let i = 0; i < 3; i += 1) {
    const a = (Math.PI / 3) * i + Math.PI / 6;
    ctx.beginPath();
    ctx.moveTo(to.x - Math.cos(a) * s, to.y - Math.sin(a) * s);
    ctx.lineTo(to.x + Math.cos(a) * s, to.y + Math.sin(a) * s);
    ctx.stroke();
  }
  ctx.drawImage(
    glowSprite(color),
    to.x - 10 * alpha - 3,
    to.y - 10 * alpha - 3,
    (10 * alpha + 3) * 2,
    (10 * alpha + 3) * 2,
  );
}

/** A clean pulse beam: wide glow halo, white core, impact star. */
function drawPulseBeam(
  ctx: CanvasRenderingContext2D,
  from: Pt,
  to: Pt,
  color: string,
  alpha: number,
): void {
  ctx.strokeStyle = withAlpha(color, alpha * 0.5);
  ctx.lineWidth = 5;
  ctx.beginPath();
  ctx.moveTo(from.x, from.y);
  ctx.lineTo(to.x, to.y);
  ctx.stroke();
  ctx.strokeStyle = withAlpha('#ffffff', alpha);
  ctx.lineWidth = 1.6;
  ctx.beginPath();
  ctx.moveTo(from.x, from.y);
  ctx.lineTo(to.x, to.y);
  ctx.stroke();
  ctx.drawImage(
    glowSprite(color),
    to.x - 14 * alpha - 4,
    to.y - 14 * alpha - 4,
    (14 * alpha + 4) * 2,
    (14 * alpha + 4) * 2,
  );
}

/**
 * A Sniper Rail pierce lance: a wide soft glow under a bright white core,
 * spanning the whole firing line from the tower out to the last enemy it
 * pierced (docs/GAME_DESIGN.md §5). A railgun streak — thicker and brighter
 * than a Pulse beam — sells that the shot punches straight through everything.
 */
function drawSniperBeam(
  ctx: CanvasRenderingContext2D,
  from: Pt,
  to: Pt,
  color: string,
  alpha: number,
): void {
  ctx.strokeStyle = withAlpha(color, alpha * 0.42);
  ctx.lineWidth = 6;
  ctx.beginPath();
  ctx.moveTo(from.x, from.y);
  ctx.lineTo(to.x, to.y);
  ctx.stroke();
  ctx.strokeStyle = withAlpha('#ffffff', alpha);
  ctx.lineWidth = 1.8;
  ctx.beginPath();
  ctx.moveTo(from.x, from.y);
  ctx.lineTo(to.x, to.y);
  ctx.stroke();
  // Terminal bloom where the lance spends itself on the last pierced enemy.
  ctx.drawImage(
    glowSprite(color),
    to.x - 14 * alpha - 4,
    to.y - 14 * alpha - 4,
    (14 * alpha + 4) * 2,
    (14 * alpha + 4) * 2,
  );
}

/** A jagged Arc Coil beam — deterministic lightning between two points. */
function drawArcBeam(
  ctx: CanvasRenderingContext2D,
  from: Pt,
  to: Pt,
  color: string,
  alpha: number,
  id: number,
): void {
  const dx = to.x - from.x;
  const dy = to.y - from.y;
  const len = Math.hypot(dx, dy) || 1;
  const nx = -dy / len;
  const ny = dx / len;
  const steps = 6;
  const drawJag = (width: number, col: string): void => {
    ctx.strokeStyle = col;
    ctx.lineWidth = width;
    ctx.beginPath();
    ctx.moveTo(from.x, from.y);
    for (let i = 1; i < steps; i += 1) {
      const t = i / steps;
      const off = (hash01(id * 31 + i) - 0.5) * 14 * Math.sin(t * Math.PI);
      ctx.lineTo(from.x + dx * t + nx * off, from.y + dy * t + ny * off);
    }
    ctx.lineTo(to.x, to.y);
    ctx.stroke();
  };
  drawJag(5, withAlpha(color, alpha * 0.45));
  drawJag(1.6, withAlpha('#f4e6ff', alpha));
  ctx.drawImage(
    glowSprite(color),
    to.x - 12 * alpha - 4,
    to.y - 12 * alpha - 4,
    (12 * alpha + 4) * 2,
    (12 * alpha + 4) * 2,
  );
}

/** Draw fading kill-burst particles as glowing sparks. */
function drawParticles(ctx: CanvasRenderingContext2D, state: GameState): void {
  if (state.particles.length === 0) return;
  ctx.save();
  ctx.translate(0, OY);
  ctx.globalCompositeOperation = 'lighter';
  for (const p of state.particles) {
    const a = clamp(p.ttlTicks / p.maxTtl, 0, 1);
    const r = (0.6 + a * 2.4) * 3.6;
    ctx.globalAlpha = a;
    ctx.drawImage(glowSprite(p.color), p.x - r, p.y - r, r * 2, r * 2);
  }
  ctx.restore();
}

// --- transient VFX (Cryo pulses + Fork splits) -------------------------------

/** A short-lived effect spawned from a tick-driven state change. */
interface Vfx {
  kind: 'cryo' | 'fork' | 'mortar' | 'shieldbreak' | 'airstrike' | 'empburst';
  /** Screen-space centre (for a Mortar / Airstrike: the detonation point). */
  x: number;
  y: number;
  /** Wall-clock birth time, seconds. */
  born: number;
  /** Outer radius the effect expands to, pixels. */
  radius: number;
  /** Mortar only: the shell's launch point, screen-space. */
  fromX?: number;
  fromY?: number;
}

/** Effect lifetimes, seconds. */
const VFX_LIFE: Record<Vfx['kind'], number> = {
  cryo: 0.55,
  fork: 0.5,
  mortar: 0.7,
  shieldbreak: 0.45,
  airstrike: 0.85,
  empburst: 0.6,
};

/** Per-Buffer `shield` value at the previous track — detects shield breaks. */
let shieldPrev = new Map<number, number>();
/** Per-ability `cooldownLeft` at the previous track — detects a fresh use. */
let abilityCdPrev = new Map<AbilityId, number>();

/** Live VFX instances. */
let vfxList: Vfx[] = [];
/** Run tick at the last track — a drop signals reset(), so tracking clears. */
let vfxPrevTick = -1;
/** Highest enemy id seen — anything above it is freshly spawned. */
let vfxMaxEnemyId = 0;
/** Highest projectile id seen — anything above it is a fresh beam. */
let vfxMaxProjId = 0;

/**
 * Diff the run against the previous frame to spawn transient VFX:
 *  - a fresh batch of `cryo` beams ⇒ a Cryo AoE frost pulse;
 *  - a fresh enemy carrying a `parentId` ⇒ a Fork split burst.
 * Entity ids are monotonic, so a high-water mark detects "new" in O(1) memory.
 */
function trackVfx(state: GameState, enemyPos: Map<number, Pt>, time: number): void {
  // Detect a reset() so stale high-water marks can't silently suppress fresh
  // VFX (e.g. after a reset() followed by a long tick() batch with no render
  // in between). Any of: the tick counter ran backwards; it is back at 0; or
  // `nextId` no longer exceeds the tracked enemy mark, i.e. ids were recycled.
  if (state.tick < vfxPrevTick || state.tick === 0 || state.nextId <= vfxMaxEnemyId) {
    vfxList = [];
    vfxMaxEnemyId = 0;
    vfxMaxProjId = 0;
    shieldPrev = new Map();
    abilityCdPrev = new Map();
  }
  vfxPrevTick = state.tick;

  // EMP Burst: its cooldown only ever *rises* on a fresh use (it counts down
  // otherwise), so a jump up is an unambiguous "fired this batch" signal — the
  // cue for the field-wide static flash. Airstrike + Overcharge are picked up
  // by their projectile / `overchargeTicksLeft` instead.
  for (const a of state.abilities) {
    const prev = abilityCdPrev.get(a.id);
    if (prev !== undefined && a.cooldownLeft > prev && a.id === 'empBurst') {
      vfxList.push({
        kind: 'empburst',
        x: PW / 2,
        y: OY + PH / 2,
        born: time,
        radius: Math.hypot(PW, PH) / 2,
      });
    }
    abilityCdPrev.set(a.id, a.cooldownLeft);
  }

  // Buffer shield breaks: a shield that fell from > 0 to 0 pops in a glassy
  // shatter. Rebuilding the map from the live roster also prunes dead ids.
  const nextShield = new Map<number, number>();
  for (const e of state.enemies) {
    if (!e.alive || e.maxShield <= 0) continue;
    const prev = shieldPrev.get(e.id);
    if (prev !== undefined && prev > 0 && e.shield <= 0) {
      const pos = enemyPos.get(e.id);
      if (pos) {
        vfxList.push({
          kind: 'shieldbreak',
          x: pos.x,
          y: pos.y,
          born: time,
          radius: enemyRadius(e.type) + 8,
        });
      }
    }
    nextShield.set(e.id, e.shield);
  }
  shieldPrev = nextShield;

  // Fork splits: new enemies carrying a parentId — one burst per parent Fork.
  let nextEnemyId = vfxMaxEnemyId;
  const forkSpots = new Map<number, Pt>();
  for (const e of state.enemies) {
    if (e.id > vfxMaxEnemyId && e.parentId !== null) {
      const pos = enemyPos.get(e.id);
      if (pos) forkSpots.set(e.parentId, pos);
    }
    if (e.id > nextEnemyId) nextEnemyId = e.id;
  }
  vfxMaxEnemyId = nextEnemyId;
  for (const pos of forkSpots.values()) {
    vfxList.push({ kind: 'fork', x: pos.x, y: pos.y, born: time, radius: 30 });
  }

  // Cryo pulses: new `cryo` beams — one pulse per emitter tile. Mortar blasts:
  // one new `mortar` beam per shot — a shell arc into an acid blast ring.
  // Airstrikes carry their own `airstrike` kind — no geometry sniffing needed.
  let nextProjId = vfxMaxProjId;
  const cryoSpots = new Map<string, Vfx>();
  for (const p of state.projectiles) {
    if (p.id > vfxMaxProjId) {
      if (p.kind === 'cryo') {
        const key = `${Math.round(p.from.x)},${Math.round(p.from.y)}`;
        if (!cryoSpots.has(key)) {
          cryoSpots.set(key, {
            kind: 'cryo',
            x: p.from.x,
            y: p.from.y + OY,
            born: time,
            radius: cryoRangeAt(state, p.from),
          });
        }
      } else if (p.kind === 'airstrike') {
        vfxList.push({
          kind: 'airstrike',
          x: p.to.x,
          y: p.to.y + OY,
          born: time,
          radius: airstrikeRadiusPx(),
        });
      } else if (p.kind === 'mortar') {
        vfxList.push({
          kind: 'mortar',
          x: p.to.x,
          y: p.to.y + OY,
          born: time,
          radius: mortarBlastAt(state, p.from),
          fromX: p.from.x,
          fromY: p.from.y + OY,
        });
      }
    }
    if (p.id > nextProjId) nextProjId = p.id;
  }
  vfxMaxProjId = nextProjId;
  for (const v of cryoSpots.values()) vfxList.push(v);

  // Age out finished effects.
  vfxList = vfxList.filter((v) => time - v.born < VFX_LIFE[v.kind]);
}

/** Pixel range of the Cryo tower whose centre is at playfield-local `from`. */
function cryoRangeAt(state: GameState, from: Pt): number {
  for (const t of state.towers) {
    if (t.type !== 'cryo') continue;
    const c = cellCenter(t.col, t.row);
    if (Math.abs(c.x - from.x) < 1 && Math.abs(c.y - from.y) < 1) return t.range * TS;
  }
  return 2.2 * TS;
}

/** Pixel blast radius of the Mortar whose centre is at playfield-local `from`. */
function mortarBlastAt(state: GameState, from: Pt): number {
  for (const t of state.towers) {
    if (t.type !== 'mortar') continue;
    const c = cellCenter(t.col, t.row);
    if (Math.abs(c.x - from.x) < 1 && Math.abs(c.y - from.y) < 1) return t.blastRadius * TS;
  }
  return 1.2 * TS;
}

/** Draw every live transient VFX. */
function drawVfx(ctx: CanvasRenderingContext2D, time: number): void {
  for (const v of vfxList) {
    const p = clamp((time - v.born) / VFX_LIFE[v.kind], 0, 1);
    if (v.kind === 'cryo') drawCryoPulse(ctx, v, p);
    else if (v.kind === 'mortar') drawMortarVfx(ctx, v, p);
    else if (v.kind === 'shieldbreak') drawShieldBreak(ctx, v, p);
    else if (v.kind === 'airstrike') drawAirstrikeVfx(ctx, v, p);
    else if (v.kind === 'empburst') drawEmpBurstVfx(ctx, v, p);
    else drawForkBurst(ctx, v, p);
  }
}

/** A Buffer shield shattering — a glassy ring bursting into sky-blue shards. */
function drawShieldBreak(ctx: CanvasRenderingContext2D, v: Vfx, p: number): void {
  const fade = 1 - p;
  const hue = CONFIG.enemies.buffer.color;
  glow(ctx, v.x, v.y, 16 * fade + 5, hue, 0.5 * fade);
  ctx.save();
  ctx.translate(v.x, v.y);
  ctx.globalCompositeOperation = 'lighter';
  // The cracked shell flashing outward.
  const rr = lerp(v.radius * 0.7, v.radius + 13, p);
  ctx.strokeStyle = withAlpha('#dff4ff', 0.7 * fade);
  ctx.lineWidth = 2 * fade + 0.5;
  ctx.beginPath();
  ctx.arc(0, 0, rr, 0, Math.PI * 2);
  ctx.stroke();
  // Glassy shards flung off the wavefront.
  const shards = 9;
  for (let i = 0; i < shards; i += 1) {
    const a = (Math.PI * 2 * i) / shards + hash01(i * 7) * 0.6;
    const inner = rr * 0.52;
    const outer = rr + 6 + hash01(i * 13) * 9;
    ctx.strokeStyle = withAlpha(i % 2 === 0 ? hue : '#eaf8ff', 0.85 * fade);
    ctx.lineWidth = 2.2 * fade + 0.4;
    ctx.beginPath();
    ctx.moveTo(Math.cos(a) * inner, Math.sin(a) * inner);
    ctx.lineTo(Math.cos(a) * outer, Math.sin(a) * outer);
    ctx.stroke();
  }
  ctx.restore();
}

/** A Mortar shell: an arcing tracer that lands in an expanding acid blast ring. */
function drawMortarVfx(ctx: CanvasRenderingContext2D, v: Vfx, p: number): void {
  const SHELL = 0.42;
  const fx = v.fromX ?? v.x;
  const fy = v.fromY ?? v.y;
  if (p < SHELL) {
    // Shell in flight, lobbed along a parabola from launch to detonation.
    const sp = p / SHELL;
    const dist = Math.hypot(v.x - fx, v.y - fy);
    const lift = Math.min(48, dist * 0.34);
    const sx = lerp(fx, v.x, sp);
    const sy = lerp(fy, v.y, sp) - lift * 4 * sp * (1 - sp);
    glow(ctx, sx, sy, 9, C.mortar, 0.85);
    ctx.fillStyle = '#ffe6cc';
    ctx.beginPath();
    ctx.arc(sx, sy, 3, 0, Math.PI * 2);
    ctx.fill();
  } else {
    // Detonation — an expanding acid-green blast ring.
    const bp = (p - SHELL) / (1 - SHELL);
    const fade = 1 - bp;
    const rr = lerp(4, v.radius, bp);
    glow(ctx, v.x, v.y, 16 * fade + 5, C.corrode, 0.55 * fade);
    ctx.save();
    ctx.translate(v.x, v.y);
    ctx.globalCompositeOperation = 'lighter';
    ctx.strokeStyle = withAlpha(C.corrode, 0.62 * fade);
    ctx.lineWidth = 3.6 * fade + 0.6;
    ctx.beginPath();
    ctx.arc(0, 0, rr, 0, Math.PI * 2);
    ctx.stroke();
    ctx.strokeStyle = withAlpha('#eaffc0', 0.42 * fade);
    ctx.lineWidth = 1.6;
    ctx.beginPath();
    ctx.arc(0, 0, rr * 0.64, 0, Math.PI * 2);
    ctx.stroke();
    // Acid droplets flung along the wavefront.
    ctx.fillStyle = withAlpha(C.corrode, 0.7 * fade);
    for (let i = 0; i < 10; i += 1) {
      const a = (Math.PI / 5) * i + bp * 0.5;
      ctx.beginPath();
      ctx.arc(Math.cos(a) * rr, Math.sin(a) * rr, 1.8 * fade + 0.6, 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.restore();
  }
}

/**
 * The Airstrike ability — a falling targeting reticle that drops onto the
 * struck tile, then detonates in an expanding orange shockwave ring (docs §15).
 */
function drawAirstrikeVfx(ctx: CanvasRenderingContext2D, v: Vfx, p: number): void {
  const RETICLE = 0.42;
  const hue = ABILITY_COLOR.airstrike;
  if (p < RETICLE) {
    // The reticle plunges toward the tile, accelerating like a falling shell.
    const sp = p / RETICLE;
    const drop = sp * sp;
    const ry = lerp(v.y - 88, v.y, drop);
    const scale = lerp(1.7, 1, drop);
    // Faint ground marker showing where the strike will land.
    ctx.save();
    ctx.translate(v.x, v.y);
    ctx.globalCompositeOperation = 'lighter';
    ctx.strokeStyle = withAlpha(hue, 0.3 + 0.25 * Math.sin(p * 36));
    ctx.lineWidth = 1.4;
    ctx.setLineDash([5, 6]);
    ctx.beginPath();
    ctx.arc(0, 0, v.radius * 0.9, 0, Math.PI * 2);
    ctx.stroke();
    ctx.setLineDash([]);
    ctx.restore();
    // Vertical incoming streak above the reticle.
    ctx.save();
    ctx.globalCompositeOperation = 'lighter';
    const streak = ctx.createLinearGradient(0, ry - 60, 0, ry);
    streak.addColorStop(0, withAlpha(hue, 0));
    streak.addColorStop(1, withAlpha(hue, 0.55));
    ctx.strokeStyle = streak;
    ctx.lineWidth = 2.2;
    ctx.beginPath();
    ctx.moveTo(v.x, ry - 60);
    ctx.lineTo(v.x, ry);
    ctx.stroke();
    // The reticle itself — a spinning crosshair ring.
    glow(ctx, v.x, ry, 16 * scale, hue, 0.6);
    ctx.translate(v.x, ry);
    ctx.rotate(p * 9);
    ctx.scale(scale, scale);
    ctx.strokeStyle = hue;
    ctx.lineWidth = 1.8;
    ctx.beginPath();
    ctx.arc(0, 0, 12, 0, Math.PI * 2);
    ctx.stroke();
    ctx.lineWidth = 2;
    for (let i = 0; i < 4; i += 1) {
      const a = (Math.PI / 2) * i;
      ctx.beginPath();
      ctx.moveTo(Math.cos(a) * 6, Math.sin(a) * 6);
      ctx.lineTo(Math.cos(a) * 17, Math.sin(a) * 17);
      ctx.stroke();
    }
    ctx.fillStyle = '#fff';
    ctx.beginPath();
    ctx.arc(0, 0, 2.4, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
    return;
  }
  // Detonation — an expanding orange shockwave ring with a hot core flash.
  const bp = (p - RETICLE) / (1 - RETICLE);
  const fade = 1 - bp;
  const rr = lerp(6, v.radius, Math.sqrt(bp));
  glow(ctx, v.x, v.y, 26 * fade + 8, hue, 0.7 * fade);
  if (bp < 0.3) glow(ctx, v.x, v.y, 20, '#fff7ec', 0.8 * (1 - bp / 0.3));
  ctx.save();
  ctx.translate(v.x, v.y);
  ctx.globalCompositeOperation = 'lighter';
  // Shockwave ring + a softer trailing ring.
  ctx.strokeStyle = withAlpha(hue, 0.72 * fade);
  ctx.lineWidth = 4.2 * fade + 0.7;
  ctx.beginPath();
  ctx.arc(0, 0, rr, 0, Math.PI * 2);
  ctx.stroke();
  ctx.strokeStyle = withAlpha('#ffe6c8', 0.5 * fade);
  ctx.lineWidth = 1.8;
  ctx.beginPath();
  ctx.arc(0, 0, rr * 0.66, 0, Math.PI * 2);
  ctx.stroke();
  // Blast shards flung along the wavefront.
  ctx.strokeStyle = withAlpha('#fff2dc', 0.85 * fade);
  ctx.lineWidth = 2.4 * fade + 0.4;
  for (let i = 0; i < 10; i += 1) {
    const a = (Math.PI / 5) * i + bp * 0.4;
    ctx.beginPath();
    ctx.moveTo(Math.cos(a) * rr * 0.78, Math.sin(a) * rr * 0.78);
    ctx.lineTo(Math.cos(a) * (rr + 9 * fade), Math.sin(a) * (rr + 9 * fade));
    ctx.stroke();
  }
  ctx.restore();
}

/**
 * EMP Burst — a brief white-blue static flash washing the whole field as every
 * Glitch is stunned (docs §15). Kept short and low-alpha so the frame stays
 * readable through it.
 */
function drawEmpBurstVfx(ctx: CanvasRenderingContext2D, v: Vfx, p: number): void {
  const col = C.emp;
  ctx.save();
  // Field-wide flash — a sharp white-blue wash that decays fast.
  const flash = Math.pow(clamp(1 - p, 0, 1), 1.5);
  ctx.globalCompositeOperation = 'lighter';
  ctx.fillStyle = withAlpha(col, 0.2 * flash);
  ctx.fillRect(0, OY, PW, PH);
  // Expanding shock ring from the field centre.
  const rr = lerp(24, v.radius, Math.sqrt(p));
  const fade = 1 - p;
  ctx.translate(v.x, v.y);
  ctx.strokeStyle = withAlpha('#eaf6ff', 0.6 * fade);
  ctx.lineWidth = 3.4 * fade + 0.6;
  ctx.beginPath();
  ctx.arc(0, 0, rr, 0, Math.PI * 2);
  ctx.stroke();
  ctx.strokeStyle = withAlpha(col, 0.45 * fade);
  ctx.lineWidth = 1.6;
  ctx.beginPath();
  ctx.arc(0, 0, rr * 1.18, 0, Math.PI * 2);
  ctx.stroke();
  // Crackling static spokes radiating from the centre.
  ctx.strokeStyle = withAlpha('#ffffff', 0.7 * fade);
  ctx.lineWidth = 1.5;
  for (let i = 0; i < 12; i += 1) {
    const a = (Math.PI / 6) * i + p * 1.5;
    const inner = rr * 0.5;
    const outer = rr + 18 * fade * (0.6 + hash01(i * 11));
    ctx.beginPath();
    ctx.moveTo(Math.cos(a) * inner, Math.sin(a) * inner);
    ctx.lineTo(Math.cos(a) * outer, Math.sin(a) * outer);
    ctx.stroke();
  }
  ctx.restore();
}

/** An expanding frost ring — the Cryo Emitter's AoE slow pulse. */
function drawCryoPulse(ctx: CanvasRenderingContext2D, v: Vfx, p: number): void {
  const fade = 1 - p;
  const rr = lerp(6, v.radius, p);
  glow(ctx, v.x, v.y, 16 * fade + 4, C.frost, 0.5 * fade);
  ctx.save();
  ctx.translate(v.x, v.y);
  ctx.globalCompositeOperation = 'lighter';
  // Expanding shockwave ring + a softer trailing ring.
  ctx.strokeStyle = withAlpha(C.frost, 0.55 * fade);
  ctx.lineWidth = 3 * fade + 0.5;
  ctx.beginPath();
  ctx.arc(0, 0, rr, 0, Math.PI * 2);
  ctx.stroke();
  ctx.strokeStyle = withAlpha('#e8ffff', 0.4 * fade);
  ctx.lineWidth = 1.5;
  ctx.beginPath();
  ctx.arc(0, 0, rr * 0.72, 0, Math.PI * 2);
  ctx.stroke();
  // Frost shards flung along the wavefront.
  ctx.strokeStyle = withAlpha('#e8ffff', 0.7 * fade);
  ctx.lineWidth = 1.6;
  for (let i = 0; i < 8; i += 1) {
    const a = (Math.PI / 4) * i + p * 0.6;
    const inner = rr * 0.82;
    ctx.beginPath();
    ctx.moveTo(Math.cos(a) * inner, Math.sin(a) * inner);
    ctx.lineTo(Math.cos(a) * (inner + 6 * fade), Math.sin(a) * (inner + 6 * fade));
    ctx.stroke();
  }
  ctx.restore();
}

/** A violet split burst where a Fork breaks into its Bit children. */
function drawForkBurst(ctx: CanvasRenderingContext2D, v: Vfx, p: number): void {
  const hue = CONFIG.enemies.fork.color;
  const fade = 1 - p;
  const rr = lerp(4, v.radius, p);
  glow(ctx, v.x, v.y, 20 * fade + 4, hue, 0.6 * fade);
  ctx.save();
  ctx.translate(v.x, v.y);
  ctx.globalCompositeOperation = 'lighter';
  // Expanding ring.
  ctx.strokeStyle = withAlpha(hue, 0.6 * fade);
  ctx.lineWidth = 2.5 * fade + 0.5;
  ctx.beginPath();
  ctx.arc(0, 0, rr, 0, Math.PI * 2);
  ctx.stroke();
  // Two halves tearing apart, plus diagonal spark shards.
  ctx.strokeStyle = withAlpha('#f0d6ff', 0.85 * fade);
  ctx.lineWidth = 2.6 * fade + 0.4;
  for (const s of [-1, 1]) {
    ctx.beginPath();
    ctx.moveTo(s * 3, 0);
    ctx.lineTo(s * rr, 0);
    ctx.stroke();
  }
  ctx.lineWidth = 1.4 * fade + 0.3;
  for (const [sx, sy] of [[-1, -1], [1, -1], [-1, 1], [1, 1]] as const) {
    ctx.beginPath();
    ctx.moveTo(sx * 3, sy * 3);
    ctx.lineTo(sx * rr * 0.7, sy * rr * 0.7);
    ctx.stroke();
  }
  ctx.restore();
}

// --- placement + overlays ----------------------------------------------------

/** The translucent tower preview that follows the pointer in placement mode. */
function drawPlacementGhost(
  ctx: CanvasRenderingContext2D,
  state: GameState,
  pointer: { x: number; y: number },
  time: number,
): void {
  const armed = state.selectedTowerType;
  if (!armed) return;
  const ly = pointer.y - OY;
  if (ly < 0 || ly > PH) return;
  const cell = pointToCell(pointer.x, ly);
  const occupied = state.towers.some((tw) => tw.col === cell.col && tw.row === cell.row);
  const maze = state.mode === 'maze';
  // Maze: a single placement classification drives both the legality check and
  // the wall-off tint, so the §16.4 flood-fill runs at most once per frame
  // (`placementReject` already ran it for the `ok` check).
  let ok: boolean;
  let wallOff = false;
  if (maze) {
    const reject = placementReject(state, armed, cell.col, cell.row);
    ok = reject === null;
    // A placement rejected specifically for sealing the Core tints warning
    // orange (§11); every other reject stays red.
    wallOff = reject === 'wallsOff';
  } else {
    ok = isBuildable(cell.col, cell.row) && !occupied && state.gold >= CONFIG.towers[armed].cost;
  }
  const color = ok ? CONFIG.towers[armed].color : wallOff ? C.warning : C.bad;
  const c = cellCenter(cell.col, cell.row);
  const cx = c.x;
  const cy = c.y + OY;

  // Maze Mode: a valid placement previews the route it would carve — the
  // dashed prospective trace makes the maze-building decision legible (§11).
  if (maze && ok) drawRoutePreview(ctx, state, armed, cell.col, cell.row, time);

  ctx.save();
  ctx.globalAlpha = 0.45 + 0.15 * Math.sin(time * 5);
  ctx.fillStyle = withAlpha(color, 0.18);
  ctx.strokeStyle = color;
  ctx.lineWidth = 1.5;
  ctx.setLineDash([4, 4]);
  roundRect(ctx, cell.col * TS + 3, OY + cell.row * TS + 3, TS - 6, TS - 6, 6);
  ctx.fill();
  ctx.stroke();
  ctx.setLineDash([]);
  ctx.fillStyle = color;
  ctx.strokeStyle = color;
  if (!ok) {
    ctx.lineWidth = 3;
    ctx.beginPath();
    ctx.moveTo(cx - 8, cy - 8);
    ctx.lineTo(cx + 8, cy + 8);
    ctx.moveTo(cx + 8, cy - 8);
    ctx.lineTo(cx - 8, cy + 8);
    ctx.stroke();
  } else if (armed === 'pulse') {
    ctx.beginPath();
    ctx.arc(cx, cy, 8, 0, Math.PI * 2);
    ctx.fill();
  } else if (armed === 'arc') {
    ctx.beginPath();
    ctx.moveTo(cx, cy - 10);
    ctx.lineTo(cx + 9, cy + 7);
    ctx.lineTo(cx - 9, cy + 7);
    ctx.closePath();
    ctx.fill();
  } else if (armed === 'wall') {
    // A solid block — the Wall ghost reads as masonry, not a tower (§16.9).
    roundRect(ctx, cx - 9, cy - 9, 18, 18, 3);
    ctx.fill();
  } else {
    ctx.beginPath();
    ctx.moveTo(cx, cy - 11);
    ctx.lineTo(cx + 11, cy);
    ctx.lineTo(cx, cy + 11);
    ctx.lineTo(cx - 11, cy);
    ctx.closePath();
    ctx.fill();
  }
  ctx.restore();
}

// --- maze route preview + readout --------------------------------------------

/** Memo for the maze route-preview A* — keyed by candidate tile + tower set. */
let routePreviewMemo: { key: string; route: Cell[] | null } | null = null;

/**
 * The A* route the maze would take if a tower stood on (col, row): the current
 * tower blockers plus that candidate tile. Memoised so a stationary pointer
 * costs one A* run, not one per frame.
 */
function prospectiveRoute(state: GameState, col: number, row: number): Cell[] | null {
  const cols = CONFIG.grid.cols;
  const rows = CONFIG.grid.rows;
  const blocked = new Set<number>();
  let sig = (col * 257 + row) >>> 0;
  for (const t of state.towers) {
    const idx = tileIndex(t.col, t.row, cols);
    blocked.add(idx);
    sig = (sig * 31 + idx) >>> 0;
  }
  const key = `${col},${row},${sig}`;
  if (routePreviewMemo && routePreviewMemo.key === key) return routePreviewMemo.route;
  blocked.add(tileIndex(col, row, cols));
  const route = findPath(SPAWN_CELL, CORE_CELL, blocked, cols, rows);
  routePreviewMemo = { key, route };
  return route;
}

/**
 * Draw the dashed prospective maze route for a valid placement preview, and
 * record its length so the route badge can show the `current → next` hint.
 * The preview is a crisp blueprint-white trace — deliberately *not* the route
 * cyan, so it reads as "the route this placement would carve" even where it
 * runs alongside the live route (docs §11). The armed tower's colour still
 * tints a soft halo as a subtle tie to what is being placed.
 */
function drawRoutePreview(
  ctx: CanvasRenderingContext2D,
  state: GameState,
  armed: TowerType,
  col: number,
  row: number,
  time: number,
): void {
  const route = prospectiveRoute(state, col, row);
  if (!route) return;
  mazePreviewLen = route.length - 1;
  ctx.save();
  ctx.translate(0, OY);
  ctx.lineJoin = 'round';
  ctx.lineCap = 'round';
  // Dark casing so the bright dashes pop over both the open field and the
  // live route, plus a soft tower-tinted halo.
  ctx.strokeStyle = 'rgba(7, 14, 30, 0.7)';
  ctx.lineWidth = 5;
  strokePolyline(ctx, route);
  ctx.globalCompositeOperation = 'lighter';
  ctx.strokeStyle = withAlpha(CONFIG.towers[armed].color, 0.18);
  ctx.lineWidth = 7;
  strokePolyline(ctx, route);
  ctx.globalCompositeOperation = 'source-over';
  // Dashed prospective trace, marching toward the Core.
  ctx.strokeStyle = withAlpha('#eafdff', 0.92);
  ctx.lineWidth = 2;
  ctx.setLineDash([5, 7]);
  ctx.lineDashOffset = -((time * 30) % 24);
  strokePolyline(ctx, route);
  ctx.setLineDash([]);
  ctx.restore();
}

/** A tiny zig-zag maze-route glyph for the route badge. */
function drawMazeGlyph(ctx: CanvasRenderingContext2D, cx: number, cy: number, color: string): void {
  ctx.save();
  ctx.strokeStyle = color;
  ctx.lineWidth = 1.6;
  ctx.lineJoin = 'round';
  ctx.lineCap = 'round';
  ctx.beginPath();
  ctx.moveTo(cx - 7, cy - 4);
  ctx.lineTo(cx - 2, cy - 4);
  ctx.lineTo(cx - 2, cy + 4);
  ctx.lineTo(cx + 4, cy + 4);
  ctx.lineTo(cx + 4, cy - 4);
  ctx.lineTo(cx + 7, cy - 4);
  ctx.stroke();
  ctx.restore();
}

/**
 * Maze Mode route-length readout — a compact neon badge, top-right of the
 * playfield. Shows the live tile count of `state.path`; while a valid
 * placement is previewed it shows `current → next`, and it flares on a
 * reshape so the player's eye catches the maze growing.
 */
function drawMazeRouteBadge(ctx: CanvasRenderingContext2D, state: GameState, time: number): void {
  const tiles = state.mazePathLength;
  const preview = mazePreviewLen >= 0 && mazePreviewLen !== tiles ? mazePreviewLen : -1;
  const flare = 1 - mazeReshapeProgress(time); // 1 just after a reshape
  const pulse = 0.5 + 0.5 * Math.sin(time * 3);
  const label = t('hud.mazeRoute');
  const num = preview >= 0 ? `${tiles} → ${preview}` : `${tiles}`;

  ctx.save();
  ctx.font = `9px ${FONT_MONO}`;
  const labelW = ctx.measureText(label).width;
  ctx.font = `700 16px ${FONT_DISPLAY}`;
  const numW = ctx.measureText(num).width;
  const pad = 12;
  const glyphW = 20;
  const bw = Math.max(labelW, numW + 32) + glyphW + pad * 2;
  const bh = 40;
  const bx = PW - 12 - bw;
  const by = OY + 12;

  // Backing panel.
  const g = ctx.createLinearGradient(0, by, 0, by + bh);
  g.addColorStop(0, 'rgba(13, 19, 35, 0.92)');
  g.addColorStop(1, 'rgba(8, 12, 24, 0.92)');
  ctx.fillStyle = g;
  roundRect(ctx, bx, by, bw, bh, 9);
  ctx.fill();
  ctx.strokeStyle = withAlpha(C.accent, 0.5 + 0.4 * flare);
  ctx.lineWidth = 1.3;
  ctx.shadowBlur = 8 + 14 * flare * (0.7 + 0.3 * pulse);
  ctx.shadowColor = C.accent;
  roundRect(ctx, bx, by, bw, bh, 9);
  ctx.stroke();
  ctx.shadowBlur = 0;

  // Maze glyph.
  drawMazeGlyph(ctx, bx + pad + 7, by + bh / 2, withAlpha(C.accent, 0.9));

  const tx = bx + pad + glyphW;
  // Label.
  ctx.textAlign = 'left';
  ctx.textBaseline = 'alphabetic';
  ctx.font = `9px ${FONT_MONO}`;
  ctx.fillStyle = C.hudMuted;
  ctx.fillText(label, tx, by + 16);
  // Tile count — flares brighter right after a reshape.
  ctx.font = `700 16px ${FONT_DISPLAY}`;
  ctx.fillStyle = C.accent;
  ctx.shadowBlur = 6 + 9 * flare;
  ctx.shadowColor = C.accent;
  ctx.fillText(num, tx, by + 33);
  ctx.shadowBlur = 0;
  // "tiles" caption trailing the number.
  ctx.font = `8px ${FONT_MONO}`;
  ctx.fillStyle = C.hudMuted;
  ctx.fillText(t('hud.tiles'), tx + numW + 6, by + 33);
  ctx.restore();
}

/** A small non-blocking hint pill shown during the build phase. */
function drawBuildHint(ctx: CanvasRenderingContext2D, state: GameState, time: number): void {
  const text =
    state.wave === 0
      ? t('hud.buildHintIntro')
      : t('hud.buildHintReinforce', { wave: state.wave + 1 });
  ctx.save();
  ctx.font = '600 12px "Share Tech Mono", ui-monospace, monospace';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  const w = ctx.measureText(text).width + 32;
  const x = PW / 2;
  const y = OY + PH - 22;
  const pulse = 0.6 + 0.4 * Math.sin(time * 3);
  ctx.fillStyle = 'rgba(8, 12, 24, 0.86)';
  roundRect(ctx, x - w / 2, y - 13, w, 26, 13);
  ctx.fill();
  ctx.strokeStyle = withAlpha(C.accent, 0.4 + 0.4 * pulse);
  ctx.lineWidth = 1.2;
  ctx.stroke();
  ctx.fillStyle = C.accent;
  ctx.fillText(text, x, y + 1);
  ctx.restore();
}

// --- Sprint 6 abilities + Overload UI ----------------------------------------

/** Overcharge window length in ticks, read from `config.abilities`. */
function overchargeDuration(): number {
  return CONFIG.abilities.find((a) => a.id === 'overcharge')?.durationTicks ?? 50;
}

/** Airstrike blast radius in pixels, read from `config.abilities`. */
function airstrikeRadiusPx(): number {
  return (CONFIG.abilities.find((a) => a.id === 'airstrike')?.radius ?? 2) * TS;
}

/** Airstrike scorch-zone lifetime in ticks, read from `config.abilities`. */
function scorchTotalTicks(): number {
  return CONFIG.abilities.find((a) => a.id === 'airstrike')?.scorchTicks ?? 120;
}

/**
 * Sprint 20 (abilities-s20b) — Airstrike scorch zones (docs/GAME_DESIGN.md
 * §15). Draw each live zone in `state.scorchZones` as a burning orange disc
 * on the playfield. The zone is the *placed* half of the proactive re-role —
 * a lasting kill-zone the player paints on a predicted chokepoint a few
 * seconds before the surge — so it MUST be clearly visible (a faint hint
 * would defeat the whole proactive plan). Each zone uses the same orange
 * accent as the Airstrike VFX (`ABILITY_COLOR.airstrike`), with intensity
 * fading from full down to ~30% across the zone's life so the player can
 * judge whether it is worth re-painting.
 */
function drawScorchZones(
  ctx: CanvasRenderingContext2D,
  state: GameState,
  time: number,
): void {
  if (state.scorchZones.length === 0) return;
  const hue = ABILITY_COLOR.airstrike;
  const total = Math.max(1, scorchTotalTicks());
  ctx.save();
  ctx.globalCompositeOperation = 'lighter';
  // Phase the ember flicker by tile so two overlapping zones do not pulse in
  // lock-step. A coarse `Math.sin(time * 9 + phase)` is enough flicker —
  // render-only, never seeds the deterministic simulation.
  for (const zone of state.scorchZones) {
    const c = cellCenter(zone.col, zone.row);
    const cx = c.x;
    const cy = c.y + OY;
    const rPx = zone.radius * TS;
    const life = clamp(zone.ticksLeft / total, 0, 1);
    // Intensity climbs sharply at the start (a fresh strike is brightest) then
    // tails off so an old zone reads as "thinning embers" — never invisible.
    const intensity = 0.35 + 0.65 * life;
    const phase = zone.col * 1.7 + zone.row * 2.3;
    const flicker = 0.85 + 0.15 * Math.sin(time * 9 + phase);
    const a = 0.22 * intensity * flicker;
    // Soft orange halo — a radial glow on the burning ground.
    glow(ctx, cx, cy, rPx * 1.05, hue, 0.45 * intensity * flicker);
    // Ember fill — a transparent disc bringing the tile-side burn into focus.
    const grad = ctx.createRadialGradient(cx, cy, rPx * 0.15, cx, cy, rPx);
    grad.addColorStop(0, withAlpha(hue, a * 1.6));
    grad.addColorStop(0.6, withAlpha(hue, a * 0.9));
    grad.addColorStop(1, withAlpha(hue, 0));
    ctx.fillStyle = grad;
    ctx.beginPath();
    ctx.arc(cx, cy, rPx, 0, Math.PI * 2);
    ctx.fill();
    // Crisp outline — a 2-px ring around the active zone, so the placement is
    // legible even on top of the path bed / a Cryo painter halo.
    ctx.strokeStyle = withAlpha(hue, 0.45 * intensity);
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    ctx.arc(cx, cy, rPx * 0.96, 0, Math.PI * 2);
    ctx.stroke();
    // A few ember sparks orbiting near the edge — pseudo-flames marking the
    // zone as ACTIVE (rather than e.g. a debug placement preview).
    ctx.strokeStyle = withAlpha('#ffe6c8', 0.55 * intensity * flicker);
    ctx.lineWidth = 1.2;
    for (let i = 0; i < 6; i += 1) {
      const a2 = (Math.PI / 3) * i + time * 0.6 + phase;
      const inner = rPx * 0.78;
      const outer = rPx * (0.96 + 0.07 * Math.sin(time * 5 + i + phase));
      ctx.beginPath();
      ctx.moveTo(cx + Math.cos(a2) * inner, cy + Math.sin(a2) * inner);
      ctx.lineTo(cx + Math.cos(a2) * outer, cy + Math.sin(a2) * outer);
      ctx.stroke();
    }
  }
  ctx.restore();
}

/** Overcharge aftershock duration in ticks, read from `config.abilities`. */
function aftershockDuration(): number {
  return CONFIG.abilities.find((a) => a.id === 'overcharge')?.aftershockTicks ?? 40;
}

/**
 * Sprint 20 (abilities-s20b) — the Overcharge aftershock brownout overlay
 * (docs/GAME_DESIGN.md §15). The proactive *cost* of the burst window: while
 * the aftershock is active every tower fires slower than normal. Drawn as a
 * brief dim purple-violet wash + a top banner counting down — distinct from
 * the §6 grid brownout (orange) and the burst window (cyan), so the player
 * can read at a glance that *they took on this debt deliberately*.
 */
function drawAftershock(
  ctx: CanvasRenderingContext2D,
  state: GameState,
  time: number,
): void {
  const intensity = clamp(state.aftershockTicksLeft / Math.max(1, aftershockDuration()), 0, 1);
  const dim = 0.5 + 0.5 * Math.sin(time * 4);
  ctx.save();
  // Dim purple-violet vignette breathing in from the playfield edges.
  const hue = '#7d4cff';
  const edge = withAlpha(hue, 0);
  const hot = withAlpha(hue, (0.07 + 0.07 * dim) * intensity);
  const gv = ctx.createLinearGradient(0, OY, 0, OY + PH);
  gv.addColorStop(0, hot);
  gv.addColorStop(0.2, edge);
  gv.addColorStop(0.8, edge);
  gv.addColorStop(1, hot);
  ctx.fillStyle = gv;
  ctx.fillRect(0, OY, PW, PH);
  // Banner — mirrors the Overcharge/brownout slot so the player learns the
  // grammar: cyan = boost, purple = debt, orange = grid sag.
  ctx.font = `700 11px ${FONT_MONO}`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  const label = t('overlay.aftershock', {
    sec: Math.ceil(state.aftershockTicksLeft / CONFIG.tickRate),
  });
  const w = ctx.measureText(label).width + 24;
  ctx.fillStyle = 'rgba(14, 8, 22, 0.85)';
  roundRect(ctx, PW / 2 - w / 2, OY + 10, w, 22, 11);
  ctx.fill();
  ctx.fillStyle = withAlpha(hue, 0.7 + 0.3 * dim);
  ctx.fillText(label, PW / 2, OY + 22);
  ctx.restore();
}

/**
 * Overcharge field VFX — a cyan power surge washing the playfield edges while
 * the boosted-efficiency window is live (docs §11 / §15). The per-tower glow is
 * added in `drawTowers`; this is the ambient field layer.
 */
function drawOvercharge(ctx: CanvasRenderingContext2D, state: GameState, time: number): void {
  const intensity = clamp(state.overchargeTicksLeft / overchargeDuration(), 0, 1);
  const pulse = 0.55 + 0.45 * Math.sin(time * 7);
  ctx.save();
  // Edge wash — a cyan vignette breathing in from the playfield border.
  const a = (0.1 + 0.12 * pulse) * intensity;
  const edge = withAlpha(C.accent, 0);
  const hot = withAlpha(C.accent, a);
  const gv = ctx.createLinearGradient(0, OY, 0, OY + PH);
  gv.addColorStop(0, hot);
  gv.addColorStop(0.16, edge);
  gv.addColorStop(0.84, edge);
  gv.addColorStop(1, hot);
  ctx.fillStyle = gv;
  ctx.fillRect(0, OY, PW, PH);
  const gh = ctx.createLinearGradient(0, 0, PW, 0);
  gh.addColorStop(0, hot);
  gh.addColorStop(0.12, edge);
  gh.addColorStop(0.88, edge);
  gh.addColorStop(1, hot);
  ctx.fillStyle = gh;
  ctx.fillRect(0, OY, PW, PH);
  // A bright energy band sweeping down the field.
  const bandY = OY + ((time * 260) % (PH + 160)) - 80;
  const band = ctx.createLinearGradient(0, bandY - 70, 0, bandY + 70);
  band.addColorStop(0, withAlpha(C.accent, 0));
  band.addColorStop(0.5, withAlpha(C.accent, 0.1 * intensity));
  band.addColorStop(1, withAlpha(C.accent, 0));
  ctx.fillStyle = band;
  ctx.fillRect(0, OY, PW, PH);
  // Top banner — the grid boost mirrors the brownout banner's slot.
  ctx.font = `700 11px ${FONT_MONO}`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  const label = t('overlay.overcharge', {
    sec: Math.ceil(state.overchargeTicksLeft / CONFIG.tickRate),
  });
  const w = ctx.measureText(label).width + 24;
  ctx.fillStyle = 'rgba(6, 16, 22, 0.85)';
  roundRect(ctx, PW / 2 - w / 2, OY + 10, w, 22, 11);
  ctx.fill();
  ctx.fillStyle = withAlpha(C.accent, 0.7 + 0.3 * pulse);
  ctx.fillText(label, PW / 2, OY + 22);
  ctx.restore();
}

/** Pulsing orange power-sag overlay while the grid is browning out. */
function drawBrownout(ctx: CanvasRenderingContext2D, state: GameState, time: number): void {
  const eff = state.power.efficiency;
  if (eff >= 1) return;
  const severity = clamp(1 - eff, 0, 1);
  const pulse = 0.45 + 0.55 * Math.abs(Math.sin(time * 4));
  ctx.save();
  const edge = withAlpha(C.warning, 0);
  const hot = withAlpha(C.warning, 0.16 + 0.22 * severity * pulse);
  const g = ctx.createLinearGradient(0, OY, 0, OY + PH);
  g.addColorStop(0, hot);
  g.addColorStop(0.12, edge);
  g.addColorStop(0.88, edge);
  g.addColorStop(1, hot);
  ctx.fillStyle = g;
  ctx.fillRect(0, OY, PW, PH);
  const gx = ctx.createLinearGradient(0, 0, PW, 0);
  gx.addColorStop(0, hot);
  gx.addColorStop(0.1, edge);
  gx.addColorStop(0.9, edge);
  gx.addColorStop(1, hot);
  ctx.fillStyle = gx;
  ctx.fillRect(0, OY, PW, PH);
  if (pulse > 0.55) {
    ctx.font = '700 11px "Share Tech Mono", ui-monospace, monospace';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    const label = t('overlay.brownout');
    const w = ctx.measureText(label).width + 24;
    ctx.fillStyle = 'rgba(10, 8, 4, 0.85)';
    roundRect(ctx, PW / 2 - w / 2, OY + 10, w, 22, 11);
    ctx.fill();
    ctx.fillStyle = C.warning;
    ctx.fillText(label, PW / 2, OY + 22);
  }
  ctx.restore();
}

// --- scanlines ---------------------------------------------------------------

let scanPattern: CanvasPattern | null = null;

/** Animated CRT scanline overlay across the playfield. */
function drawScanlines(ctx: CanvasRenderingContext2D, time: number): void {
  if (!scanPattern) {
    const tile = document.createElement('canvas');
    tile.width = 1;
    tile.height = 3;
    const tg = tile.getContext('2d');
    if (tg) {
      tg.fillStyle = 'rgba(0, 0, 0, 0.20)';
      tg.fillRect(0, 0, 1, 1);
      scanPattern = ctx.createPattern(tile, 'repeat');
    }
  }
  ctx.save();
  if (scanPattern) {
    ctx.fillStyle = scanPattern;
    ctx.fillRect(0, OY, PW, PH);
  }
  const bandY = OY + ((time * 90) % (PH + 120)) - 60;
  const band = ctx.createLinearGradient(0, bandY - 60, 0, bandY + 60);
  band.addColorStop(0, withAlpha(C.accent, 0));
  band.addColorStop(0.5, withAlpha(C.accent, 0.05));
  band.addColorStop(1, withAlpha(C.accent, 0));
  ctx.fillStyle = band;
  ctx.fillRect(0, OY, PW, PH);
  ctx.restore();
}
